/**
 * BuildMealModal - Modal para construir comidas paso a paso
 * Extraído de NutritionPage.jsx para mejor mantenibilidad
 */
import React, { useState, useEffect, useRef } from 'react';
import PeriSugerencias from './PeriSugerencias';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { toast } from 'sonner';
import { Search, X, Plus, Minus, Star, ChevronUp, ChevronDown } from 'lucide-react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { MARGEN } from '../../lib/exceso';
import { celdaDeLaVentana } from '../../lib/estadoDelMacro';
import { cantidadDelOchenta, cruzoElOchenta, EL_OCHENTA } from '../../lib/elOchenta';
import { letreroDelPaso } from '../../lib/elLetreroDelPaso';
import { num1 } from '../../lib/numeros';
import { leerCantidad, avisoRazonable, avisoDelTope, AVISO_NEGATIVO } from '../../lib/cantidades';
import { cantidadConTope, AVISO_TOPE_GRASA_POST } from '../../lib/topeGrasaPost';
import { escalarMacros } from '../../lib/escalarMacros';
import { FOOD_FAVORITES_UI } from './SearchFoodModal';
import SinResultados from './SinResultados';
import SuggestFoodModal from './SuggestFoodModal';
import {
    faStopwatch20,
    faEgg, faBacon, faBurger, faDove, faCow, faPiggyBank, faDrumstickBite,
    faFish, faCheese, faJar, faJarWheat, faSeedling, faBowlRice,
    faBreadSlice, faPlateWheat, faBowlFood, faCarrot, faAppleWhole,
    faLeaf, faBottleWater, faBolt, faBlender, faPizzaSlice, faCookie,
    faPepperHot, faBottleDroplet, faOilCan,
    faCircleInfo, faDroplet, faCookieBite, faScaleBalanced,
    faSquareCheck, faWheatAwnCircleExclamation, faSnowflake, faRing,
    faThumbsUp, faClock, faFireBurner, faMedal,
    faTrowelBricks, faCandyCane, faIceCream, faSmog, faUtensils, faMartiniGlassCitrus,
} from '@fortawesome/free-solid-svg-icons';

// Calma config T.macros.margenValido: peri (intra/post) meals have NO grasas objetivo, but
// macrosRestantes adds this fat slack so a little incidental fat is allowed/sized for. Fat
// budget = (target.G - served.G) + MARGEN_VALIDO, used by the engine to size AND order. Must
// match backend calma_suggest.MARGEN_VALIDO. Verified vs the Calma bundle 2026-06-14.
const MARGEN_VALIDO = 4;

// LA BARRA DE LOS FILTROS RECOGIDOS (opción A, 24-09). Enseña lo que está marcado sin
// ocupar más de una línea por texto, aunque se marquen muchas (Francisco, 23-09: «si
// selecciona muchas categorías el texto se empieza a apilar... hasta perderse»): como mucho
// tres círculos y un «+N», y los nombres solo si son una o dos; si son más, el número.
const icono = (ic) => (ic && typeof ic === 'object' && (ic.iconName || ic.prefix))
    ? <FontAwesomeIcon icon={ic} className="w-3.5 h-3.5" />
    : null;
const resumir = (lista, palabra) => (lista.length <= 2
    ? lista.map(x => x.short || x.label).join(' + ')
    : `${lista.length} ${palabra}`);
const BarraDeFiltros = ({ categorias, preparaciones, abierta, haTocado, onToggle }) => {
    const marcados = [...categorias, ...preparaciones];
    const linea1 = categorias.length ? resumir(categorias, 'categorías') : 'Elige una categoría';
    const linea2 = preparaciones.length
        ? resumir(preparaciones, 'preparaciones')
        // Abierta, la línea de ayuda del panel ya dice cómo se cambia: aquí, cómo se recoge.
        : abierta ? 'Toca para recoger los filtros'
        : (haTocado ? 'Toca para cambiar de categoría o preparación' : 'Toca para cambiar de categoría');
    return (
        <button type="button" onClick={onToggle} aria-expanded={abierta} data-testid="barra-filtros"
            title={marcados.map(m => m.label).join(', ')}
            className="w-full flex items-center gap-2 rounded-xl border bg-muted/40 px-2.5 py-2 text-left">
            <span className="flex items-center gap-1 flex-shrink-0">
                {marcados.slice(0, 3).map(m => (
                    <span key={m.value || m.id}
                        className="w-7 h-7 rounded-full border-2 border-brand text-brand bg-brand/10 grid place-items-center">
                        {icono(m.icon)}
                    </span>
                ))}
                {marcados.length > 3 && (
                    <span className="w-7 h-7 rounded-full bg-muted text-[11px] font-bold grid place-items-center"
                        data-testid="filtros-mas">+{marcados.length - 3}</span>
                )}
            </span>
            <span className="flex-1 min-w-0">
                <span className="block truncate text-sm font-medium text-foreground" data-testid="filtros-categorias">{linea1}</span>
                <span className="block truncate text-[11px] text-muted-foreground" data-testid="filtros-preparaciones">{linea2}</span>
            </span>
            <ChevronDown className={`w-4 h-4 flex-shrink-0 text-muted-foreground transition-transform ${abierta ? 'rotate-180' : ''}`} />
        </button>
    );
};

const FREQUENT_CATEGORY = {
    id: '__frequent__',
    value: '__frequent__',
    label: 'Alimentos frecuentes',
    short: 'Frecuentes',
    icon: faStopwatch20,
};

// Nombre corto que se ve dentro de la pill. Los nombres de Calma son frases enteras
// ("Cacao en polvo y azúcares de todo tipo, chucherías y miel") y con trece categorías
// en 390 px no cabe ninguna: el largo se queda en el tooltip y en el aria-label.
// Abreviaturas coherentes con los chips del buscador de alimentos ("Prot. polvo",
// "Beb. vegetales"). Clave = id de la categoría, compartido entre pasos.
const NOMBRE_CORTO = {
    __frequent__: 'Frecuentes',
    // Proteínas
    huevos: 'Huevos',
    embutidos: 'Embutidos',
    aves: 'Aves',
    vacuno: 'Vacuno',
    cerdo: 'Cerdo',
    otras_carnes: 'Otras carnes',
    carnes_blancas: 'Carnes blancas',
    carnes_rojas: 'Carnes rojas',
    casqueria: 'Casquería',
    pescados: 'Pescados',
    lacteos: 'Lácteos',
    proteina: 'Prot. polvo',
    proteina_polvo: 'Prot. polvo',
    proteina_vegetal: 'Prot. vegetal',
    legumbres: 'Legumbres',
    // Hidratos y acompañamientos
    arroces: 'Arroces',
    panes: 'Panes',
    pan: 'Panes',
    cereales: 'Cereales',
    pasta: 'Pasta',
    tuberculos: 'Tubérculos',
    fruta: 'Fruta',
    verduras: 'Verduras',
    cremas_arroz: 'Cremas y tortas',
    sustitutivos: 'Sustitutivos',
    sopas: 'Sopas y caldos',
    cocina_esp: 'Cocina española',
    pizza: 'Pizza y lasaña',
    comida_rapida: 'Comida rápida',
    aperitivos: 'Aperitivos',
    // Bebidas
    beb_vegetales: 'Beb. vegetales',
    bebidas: 'Refrescos y café',
    isotonicas: 'Isotónicas',
    aminoacidos: 'Aminoácidos',
    // Dulces
    cremas: 'Bollería',
    bolleria: 'Bollería',
    chocolates: 'Chocolates',
    barritas: 'Barritas',
    helados: 'Helados',
    postres: 'Postres',
    cacao: 'Cacao y azúcar',
    azucar: 'Cacao y azúcar',
    // Grasas y otros
    grasas_buenas: 'Grasas buenas',
    grasas_todo: 'Otras grasas',
    grasas: 'Otras grasas',
    salsas: 'Salsas',
    superalimentos: 'Superalimentos',
};

const conNombreCorto = (cats) => cats.map(c => ({
    ...c,
    short: c.short || NOMBRE_CORTO[c.id] || c.label,
}));
import CategoryRail from './CategoryRail';
import NombreConEtiqueta from './NombreConEtiqueta';
import { pasoDelCampo } from '../../lib/pasoDeCantidad';
import { alimentosVisibles, CUANTOS as CUANTOS_ALIMENTOS } from '../../lib/alimentosVisibles';
import { PREFERENCE_CATEGORIES } from './PreferencesSetup';
import { alTerminarElRecorrido } from '../../lib/finDelRecorrido';

// Categories - Step 1 (Proteínas) - EXACT Calma T.categorias.categoriasProteinas order:
// ["1","2.1","2.2","2.3","2.4","45","3","5","30","28","10"] (single-code chips).
const PROTEIN_CATEGORIES = [
    { id: 'huevos',           value: 'huevos',           label: 'Huevos y derivados',                    emoji: '🥚', icon: faEgg,           prefixes: ['1'] },
    { id: 'embutidos',        value: 'embutidos',        label: 'Embutidos',                             emoji: '🥓', icon: faBacon,         prefixes: ['2.1'] },
    { id: 'aves',             value: 'aves',             label: 'Aves',                                  emoji: '🍗', icon: faDove,          prefixes: ['2.2'] },
    { id: 'vacuno',           value: 'vacuno',           label: 'Vacuno o buey',                         emoji: '🥩', icon: faCow,           prefixes: ['2.3'] },
    { id: 'cerdo',            value: 'cerdo',            label: 'Cerdo',                                 emoji: '🐷', icon: faPiggyBank,     prefixes: ['2.4'] },
    { id: 'otras_carnes',     value: 'otras_carnes',     label: 'Otras carnes',                          emoji: '🍖', icon: faDrumstickBite, prefixes: ['45'] },
    { id: 'pescados',         value: 'pescados',         label: 'Pescados y mariscos',                   emoji: '🐟', icon: faFish,          prefixes: ['3'] },
    { id: 'lacteos',          value: 'lacteos',          label: 'Lácteos y derivados',                   emoji: '🧀', icon: faCheese,        prefixes: ['5'] },
    { id: 'proteina_polvo',   value: 'proteina_polvo',   label: 'Proteína en polvo y barritas proteicas', emoji: '🥤', icon: faJar,          prefixes: ['30'] },
    { id: 'proteina_vegetal', value: 'proteina_vegetal', label: 'Proteína vegetal',                      emoji: '🌱', icon: faJarWheat,      prefixes: ['28'] },
    { id: 'legumbres',        value: 'legumbres',        label: 'Legumbres',                             emoji: '🫘', icon: faSeedling,      prefixes: ['10'] },
];

// Categories - Step 2 (Acompañamientos) - EXACT Calma T.categorias.categoriasHidratos order:
// ["21","8","7","22","9","11","13","10","5","24","19","27","32","43","44","37","38","39","16","17"].
const SIDE_CATEGORIES = [
    { id: 'arroces',       value: 'arroces',       label: 'Arroces y derivados',                                      emoji: '🍚', icon: faBowlRice,          prefixes: ['21'] },
    { id: 'panes',         value: 'panes',         label: 'Panes y tortillas de trigo',                               emoji: '🍞', icon: faBreadSlice,        prefixes: ['8'] },
    { id: 'cereales',      value: 'cereales',      label: 'Cereales (excepto arroz)',                                 emoji: '🌾', icon: faPlateWheat,        prefixes: ['7'] },
    { id: 'pasta',         value: 'pasta',         label: 'Pasta, quinoa y derivados',                                emoji: '🍝', icon: faBowlFood,          prefixes: ['22'] },
    { id: 'tuberculos',    value: 'tuberculos',    label: 'Tubérculos y derivados',                                   emoji: '🥔', icon: faCarrot,            prefixes: ['9'] },
    { id: 'fruta',         value: 'fruta',         label: 'Fruta, zumo, potitos y mermeladas',                        emoji: '🍎', icon: faAppleWhole,        prefixes: ['11'] },
    { id: 'verduras',      value: 'verduras',      label: 'Verduras y hortalizas',                                    emoji: '🥬', icon: faLeaf,              prefixes: ['13'] },
    { id: 'legumbres',     value: 'legumbres',     label: 'Legumbres',                                                emoji: '🫘', icon: faSeedling,          prefixes: ['10'] },
    { id: 'lacteos',       value: 'lacteos',       label: 'Lácteos y derivados',                                      emoji: '🧀', icon: faCheese,            prefixes: ['5'] },
    { id: 'beb_vegetales', value: 'beb_vegetales', label: 'Bebidas vegetales',                                        emoji: '🥛', icon: faBottleWater,      prefixes: ['24'] },
    { id: 'bebidas',       value: 'bebidas',       label: 'Bebidas energéticas, refrescos y cafés',                   emoji: '🥤', icon: faBolt,              prefixes: ['19'] },
    { id: 'sustitutivos',  value: 'sustitutivos',  label: 'Sustitutivos de comidas',                                  emoji: '🥤', icon: faBlender,           prefixes: ['27'] },
    { id: 'pizza',         value: 'pizza',         label: 'Pizza, lasaña, empanadas y empanadillas',                  emoji: '🍕', icon: faPizzaSlice,        prefixes: ['32'] },
    { id: 'cremas',        value: 'cremas',        label: 'Bollería, galletas, barritas energéticas, chocolate y chocolatinas', emoji: '🍫', icon: faCookie, prefixes: ['43'] },
    { id: 'helados',       value: 'helados',       label: 'Helados y postres',                                        emoji: '🍨', icon: faIceCream,          prefixes: ['44'] },
    { id: 'cacao',         value: 'cacao',         label: 'Cacao en polvo y azúcares de todo tipo, chucherías y miel', emoji: '🍯', icon: faCandyCane,        prefixes: ['37'] },
    { id: 'aperitivos',    value: 'aperitivos',    label: 'Aperitivos',                                               emoji: '🥨', icon: faMartiniGlassCitrus, prefixes: ['38'] },
    { id: 'cocina_esp',    value: 'cocina_esp',    label: 'Cocina tradicional española',                              emoji: '🥘', icon: faUtensils,         prefixes: ['39'] },
    { id: 'salsas',        value: 'salsas',        label: 'Salsas, siropes y konjac',                                 emoji: '🥫', icon: faPepperHot,         prefixes: ['16'] },
    { id: 'grasas',        value: 'grasas',        label: 'Alimentos ricos en grasas de todo tipo',                   emoji: '🫒', icon: faBottleDroplet,     prefixes: ['17'] },
];

// Step 3 - fat sources
const FAT_CATEGORIES = [
    { id: 'grasas_buenas', value: 'grasas_buenas', label: 'Grasas de buena calidad', icon: faBottleDroplet, prefixes: ['42'] },
    { id: 'grasas_todo',   value: 'grasas_todo',   label: 'Grasas',                  icon: faOilCan,        prefixes: ['17'] },
    { id: 'lacteos',       value: 'lacteos',       label: 'Lácteos',                 icon: faCheese,        prefixes: ['5'] },
    { id: 'huevos',        value: 'huevos',        label: 'Huevos',                  icon: faEgg,           prefixes: ['1'] },
];

// INTRA categories - Calma categoriasIntraentreno: ["18","41"] (chips hidden, pre-active)
const INTRA_CATEGORIES = [
    { id: 'isotonicas',  value: 'isotonicas',  label: 'Intraentrenamiento',       emoji: '💧', icon: faBottleWater, prefixes: ['18'] },
    { id: 'aminoacidos', value: 'aminoacidos', label: 'Aminoacidos para entrenar', emoji: '⚡', icon: faTrowelBricks, prefixes: ['41'] },
];

// POST categories - single list, Calma categoriasPostentreno order:
// ["4","5","46","7","8","11","27","24","19","37","36","16"]
const POST_CATEGORIES = [
    { id: 'proteina',      value: 'proteina',      label: 'Proteínas en polvo',                                        emoji: '💪', icon: faJar,        prefixes: ['4'] },
    { id: 'lacteos',       value: 'lacteos',       label: 'Lácteos y derivados',                                       emoji: '🧀', icon: faCheese,     prefixes: ['5'] },
    { id: 'cremas_arroz',  value: 'cremas_arroz',  label: 'Cremas y tortas de arroz',                                  emoji: '🍚', icon: faBowlRice,   prefixes: ['46'] },
    { id: 'cereales',      value: 'cereales',      label: 'Cereales (excepto arroz)',                                  emoji: '🌾', icon: faPlateWheat, prefixes: ['7'] },
    { id: 'pan',           value: 'pan',           label: 'Panes y tortillas de trigo',                                emoji: '🍞', icon: faBreadSlice, prefixes: ['8'] },
    { id: 'fruta',         value: 'fruta',         label: 'Fruta, zumo, potitos y mermeladas',                         emoji: '🍎', icon: faAppleWhole, prefixes: ['11'] },
    { id: 'sustitutivos',  value: 'sustitutivos',  label: 'Sustitutivos de comidas',                                   emoji: '🥤', icon: faBlender,    prefixes: ['27'] },
    { id: 'beb_vegetales', value: 'beb_vegetales', label: 'Bebidas vegetales',                                         emoji: '🥛', icon: faBottleWater, prefixes: ['24'] },
    { id: 'bebidas',       value: 'bebidas',       label: 'Bebidas energéticas, refrescos y cafés',                    emoji: '⚡', icon: faBolt,       prefixes: ['19'] },
    { id: 'azucar',        value: 'azucar',        label: 'Cacao en polvo y azúcares de todo tipo, chucherías y miel', emoji: '🍯', icon: faCandyCane,  prefixes: ['37'] },
    { id: 'postres',       value: 'postres',       label: 'Postres',                                                   emoji: '🍮', icon: faIceCream,   prefixes: ['36'] },
    { id: 'salsas',        value: 'salsas',        label: 'Salsas, siropes y konjac',                                  emoji: '🥫', icon: faPepperHot,  prefixes: ['16'] },
];

// Ordered to match Calma's `A.preparaciones`:
// [GEN, PRO, FRE, CGE, AHU, LAT, POL, PRE, HAM, SNA, MIN, YCO, UNI, YA, SGL]
const PREPARATIONS = [
    { value: 'GEN', label: 'Sin marca',                                short: 'Sin marca',     icon: faCircleInfo },
    { value: 'PRO', label: 'Marca recomendada con descuento especial', short: 'Recomendada',  icon: faMedal },
    { value: 'FRE', label: 'Frescos',                                  short: 'Frescos',      icon: faDroplet },
    { value: 'CGE', label: 'Congelados',                               short: 'Congelados',   icon: faSnowflake },
    { value: 'AHU', label: 'Ahumados',                                 short: 'Ahumados',     icon: faSmog },
    { value: 'LAT', label: 'Conservas',                                short: 'Conservas',    icon: faRing },
    { value: 'POL', label: 'En Polvo',                                 short: 'En polvo',     icon: faJar },
    { value: 'PRE', label: 'Preparado',                                short: 'Preparado',    icon: faThumbsUp },
    { value: 'HAM', label: 'Carne picada y hamburguesas',              short: 'Carne picada', icon: faBurger },
    { value: 'SNA', label: 'Snacks fáciles de transportar',            short: 'Para llevar',  icon: faCookieBite },
    { value: 'MIN', label: '1 Minuto al micro y listo',                short: 'Al micro',     icon: faClock },
    { value: 'YCO', label: 'Ya cocinados',                             short: 'Ya cocinado',  icon: faFireBurner },
    { value: 'UNI', label: 'Ya pesado',                                short: 'Ya pesado',    icon: faScaleBalanced },
    { value: 'YA',  label: 'Listo para comer',                         short: 'Listo',        icon: faSquareCheck },
    { value: 'SGL', label: 'Sin gluten',                               short: 'Sin gluten',   icon: faWheatAwnCircleExclamation },
];

// Food emojis
const FOOD_EMOJIS = {
    '2': '🥩', '3': '🐟', '1': '🥚', '5': '🥛', '4': '💪',
    '7': '🌾', '8': '🍞', '21': '🍚', '22': '🍝', '9': '🥔',
    '10': '🫘', '11': '🍎', '13': '🥦', '17': '🫒', '17.2': '🥜',
    '16': '🥫', '24': '🥤', '42': '🥑', '28': '🌱', '6': '🌿',
    '32': '🍕', '39': '🥘', '49': '🍔', '50': '🌮', '53': '🍱',
    'default': '🍽️'
};

const getFoodEmojiLocal = (categorias) => {
    if (!categorias) return FOOD_EMOJIS.default;
    const mainCat = categorias.split(' | ')[0]?.split('.')[0];
    return FOOD_EMOJIS[mainCat] || FOOD_EMOJIS.default;
};

const BuildMealModal = ({
    open,
    mealKey,
    mode = 'normal',
    onClose,
    getMealTarget,
    mealInfo,
    api,
    tipoDia,
    mealsData,
    // LAS COMIDAS DEL REPARTO DE HOY, en su orden. `mealsData` guarda de más (las comidas
    // que el reparto ya no enseña siguen ahí con sus alimentos), y esta ventana calibraba
    // el día con TODAS: otro total de familia, otro tramo y otros macros que los de la
    // tarjeta. Ver el efecto de `/calibrar-dia` (Francisco, 13-09-2026).
    mealOrder = null,
    setMealsData,
    setMealMode,
    getFoodEmoji,
    userPreferences = [],
    avoidedCategories = [],
    // Lo que el día lleva ya de cada familia calibrada. Sin esto esta ventana calculaba con
    // el motor de antes de la calibración y enseñaba números que cambiaban al guardar
    // (punto 135 del 26-08): ver `getDiaParams`.
    acumFamilias = null,
    // Al guardar, (mealKey, alimentos): el intra pregunta ahí si se deja puesto (27-09).
    onGuardado = null,
}) => {
    const [paso, setPaso] = useState(1);
    const [tempFoods, setTempFoods] = useState([]);
    const [addedOpen, setAddedOpen] = useState(false);  // collapsible "añadidos" bar (closed → list keeps space)
    const [qtyDraft, setQtyDraft] = useState({});  // buffer del input de cantidad por índice (permite teclear libre)

    // POR DEFECTO, LOS FRECUENTES (Jesús, 10-09, regla 4). La pantalla abría con la lista
    // vacía y un «selecciona una categoría»: el cliente entraba a montar su comida y lo
    // primero que veía era un hueco. Ahora arranca con lo que suele poner ahí, y la línea
    // de «toca un icono para cambiar de categoría» le dice que puede irse a otra.
    //
    // Y DESDE EL 24-09, NINGUNA MARCADA (Francisco: «que alimentos frecuentes ya no esté
    // preseleccionado»). Manda lo más reciente sobre la regla 4 del 10-09: se abre sin
    // categoría y con los filtros a la vista para que elija.
    const [selectedCategories, setSelectedCategories] = useState([]);
    // Si ya ha tocado una categoría él (para la línea «toca un icono para cambiar»).
    const [haTocadoCategoria, setHaTocadoCategoria] = useState(false);
    // «Mostrar más» de la lista cuando ya hay algo añadido (lib/alimentosVisibles).
    const [verTodos, setVerTodos] = useState(false);
    // LOS FILTROS, RECOGIDOS EN UNA BARRA (opción A del artefacto del 23-09, «Añadir
    // ingrediente, con más lista»; Francisco, 24-09: «aplicamos la opción 1»). En el teléfono
    // categorías, la línea de ayuda y preparación se comían 275 px: se veía alimento y medio.
    // Empiezan recogidos en el teléfono y abiertos en pantallas anchas, donde sobra sitio.
    // Y DESPLEGADAS SIEMPRE DE PRIMERAS, también en el teléfono (Jesús, reunión del 25-09:
    // «ese desplegable va a estar siempre desplegado de primeras»). Se recogen a mano.
    // ...Y RECOGIDAS DE PRIMERAS OTRA VEZ (Jesús, 26-09, WhatsApp y audio: «cuando haga una dieta
    // no se desplieguen los filtros de primeras»; «que salga ya recogido, y si quiero el filtro
    // lo señalo; así se ven desplegados los alimentos que haya»). Revierte lo de la reunión del
    // 25-09: manda lo más reciente. Para que haya alimentos a la vista sin tocar nada, se abre
    // con Frecuentes marcada (como en su captura), que revierte el «ninguna marcada» del 24-09.
    const [filtrosAbiertos, setFiltrosAbiertos] = useState(false);
    const [categoryFoods, setCategoryFoods] = useState([]);
    const [loadingFoods, setLoadingFoods] = useState(false);

    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    // Cuántos coincidían con lo escrito pero no salen porque te pasarías (24-09, como Calma).
    const [fueraPorPasarse, setFueraPorPasarse] = useState(0);
    const [isSearching, setIsSearching] = useState(false);
    // PEDIR UN ALIMENTO SIN SALIR DE LA COMIDA (Jesús, 9-09). Antes, al no encontrar nada,
    // se le decía «puedes pedirlo desde Alimentos»: otra pantalla, repetir la búsqueda y
    // dejar a medias la comida que estaba montando.
    const [pedirAbierto, setPedirAbierto] = useState(false);
    // GUARDAR, CON CONFIRMACIÓN (Jesús, 10-09, «Y guardar, con confirmación»).
    //
    //     «Hoy se guarda y se cierra de golpe. Una confirmación le dice qué se lleva
    //     -- cuántos ingredientes y si vale -- y le deja volver sin perder nada.»
    //
    // Y ahí, y SÓLO ahí, el reparo de que no llegue: «Esta comida se queda corta de
    // proteína. ¿La dejas así?» -- «es el único sitio donde ese aviso hace falta», así que
    // no se repite arriba mientras monta.
    const [confirmando, setConfirmando] = useState(false);
    // El cupo, para enseñarlo junto al botón de pedir. Se pide una vez al abrir la comida:
    // si el número llegase sólo al abrir el formulario, se enteraría después de decidir.
    const [peticionesQueQuedan, setPeticionesQueQuedan] = useState(null);
    // EL TURNO DE LA BÚSQUEDA. Cada tecla lanzaba su petición y ganaba la que llegase
    // ÚLTIMA, no la última escrita: al teclear «pechuga» la respuesta de «pe» -- que trae
    // medio catálogo y tarda más -- pisaba a la de «pechuga» y en pantalla quedaban Pepsi
    // Max y Pepitas. Ese era el fallo 3 de Jesús. Cada búsqueda coge número y solo se pinta
    // la que sigue siendo la actual.
    const turnoBusqueda = useRef(0);

    const [selectedFood, setSelectedFood] = useState(null);
    const [adjustedQuantity, setAdjustedQuantity] = useState(0);
    const [adjustedMacros, setAdjustedMacros] = useState({ P: 0, H: 0, G: 0 });

    const [favorites, setFavorites] = useState(new Set());

    // Preparation filter
    const [selectedPreparations, setSelectedPreparations] = useState([]);
    const [availablePreps, setAvailablePreps] = useState([]);

    const isIntraMode = mode === 'intra';
    const isPostMode = mode === 'post';
    const isPeriMode = isIntraMode || isPostMode;
    // Manual mode (per meal): no auto-quantity, no 80% progression, no suggestions, free
    // amount, foods alphabetical, no min-quantity block. Peri meals never use it.
    const isManual = !isPeriMode && !!mealKey && mealsData[mealKey]?.modo === 'manual';

    const target = mealKey ? getMealTarget(mealKey) : { P: 0, H: 0, G: 0 };
    // Calma's macroEnIngredientes sums UNROUNDED per-food contributions (raw post-regla
    // macros × quantity). The stored macros_efectivos are rounded to 1 decimal for display;
    // summing those loses ~0.05 g/food, enough to flip a floor() in the suggested quantity
    // (e.g. tikka H = 26.5×1.37 = 36.305 → remaining 10.495, not 10.5 → Tasty rice 13 g not
    // 14 g) and reorder the list. Rebuild the contribution from the raw fields, gated by
    // macros_efectivos so regla-25% zeroed macros stay zero; fall back to the rounded
    // efectivos when raw fields are absent (older saved diets).
    /**
     * LO QUE APORTA UN ALIMENTO DE LA VENTANA: LOS MACROS EFECTIVOS, Y NADA MÁS.
     *
     * Esto rehacía la cuenta desde los macros de la etiqueta (`f.proteinas * factor`) y sólo
     * usaba los efectivos como interruptor de encendido, para no perder decimales por el
     * redondeo. Con la calibración del día eso ya no vale: el crudo no sabe en qué tramo
     * está el día, así que reconstruirlo da otro número. Las almendras del punto 135 lo
     * enseñaban de las dos formas a la vez en la misma pantalla -- el resumen de abajo
     * decía «P 3» y la barra de arriba «40 g» sin servido --, porque el alimento llega del
     * servidor con la proteína ya puesta a cero por la regla de categoría y el crudo valía 0.
     *
     * Los efectivos vienen redondeados a la décima, que es exactamente lo que se escribe en
     * pantalla en todas partes. La precisión que se pierde no se ve; la contradicción sí.
     */
    const foodContrib = (f) => {
        const ef = f.macros_efectivos || {};
        return { P: ef.P || 0, H: ef.H || 0, G: ef.G || 0 };
    };
    const sumContrib = (list, acc) => list.reduce((a, f) => {
        const c = foodContrib(f);
        return { P: a.P + c.P, H: a.H + c.H, G: a.G + c.G };
    }, acc);
    // tempFoods se precarga con los alimentos ya guardados de la comida (ver efecto de apertura),
    // así que ya representa el TOTAL de la comida; no sumamos los existentes aparte (se duplicarían).
    const served = sumContrib(tempFoods, { P: 0, H: 0, G: 0 });
    const remaining = {
        P: Math.max(0, target.P - served.P),
        H: Math.max(0, target.H - served.H),
        G: Math.max(0, target.G - served.G)
    };

    useEffect(() => {
        const loadFavorites = async () => {
            try {
                const res = await api('/api/favorites');
                setFavorites(new Set((res.favorites || []).map(String)));
            } catch (e) { /* ignore */ }
        };
        if (open && FOOD_FAVORITES_UI) loadFavorites();
    }, [open]); // eslint-disable-line

    // EL RECORRIDO ACABA EN NUTRICIÓN (Jesús, 29-09: «te deja dentro de una comida y tiene
    // que dejarte dentro de la parte de nutrición»). Sus últimas paradas van aquí dentro; al
    // terminar (Listo o Saltar) la ventana se cierra como con «Cancelar». El recorrido no
    // mete alimentos, así que no se pierde nada (lib/finDelRecorrido.js).
    const onCloseRef = useRef(onClose);
    onCloseRef.current = onClose;
    useEffect(() => {
        if (!open) return undefined;
        return alTerminarElRecorrido(() => onCloseRef.current?.());
    }, [open]);

    // El cupo de peticiones, al abrir la comida.
    useEffect(() => {
        if (!open) return;
        let vivo = true;
        api('/api/calculator/food-suggestions/restantes')
            .then(r => { if (vivo) setPeticionesQueQuedan(r?.restantes ?? null); })
            .catch(() => { /* si no llega, el botón sale igual y sin número */ });
        return () => { vivo = false; };
    }, [open]); // eslint-disable-line

    const toggleFavorite = async (foodId) => {
        const fid = Number(foodId);
        const isFav = favorites.has(String(foodId));
        try {
            if (isFav) {
                await api(`/api/favorites/${fid}`, { method: 'DELETE' });
                setFavorites(prev => { const s = new Set(prev); s.delete(String(foodId)); return s; });
            } else {
                await api(`/api/favorites/${fid}`, { method: 'POST' });
                setFavorites(prev => new Set(prev).add(String(foodId)));
            }
        } catch (e) { /* ignore */ }
    };

    // EL MARGEN, EL DE LA CASA Y NO UN 4 SUELTO. Es el `margenValido` de Calma y el que
    // aplica `lib/exceso` en el resto de la app: escrito aquí a mano, cambiarlo obligaba a
    // acertar en dos sitios.
    const MARGEN_VALIDO = MARGEN;

    // ─────────────────────────────────────────────────────────────────────────────────
    // LA PALABRA YA NO SE ESCRIBE AQUÍ (Francisco, 13-09-2026).
    //
    //     «Y falla: "cuadrado" en la tarjeta y "Válido" en el diálogo.»
    //
    // Esta celda tenía su propia copia de la escalera de estados -- suelo del cuadrado,
    // margen de 4, «faltan»/«sobran» --, y una copia siempre acaba separándose del
    // original. Se había separado en UNA cosa: `lib/estadoDelMacro` enseña la décima del
    // cuadrado desde el 5-09 («cuadrado −0,4», ficha 12 de Jesús) y aquí «cuadrado» salía
    // siempre a secas. Con la misma comida delante, la tarjeta decía «cuadrado −0,4» y el
    // diálogo «cuadrado»: el mismo plato leído distinto según la ventana, que es justo lo
    // que la regla prohíbe.
    //
    // Ahora la palabra la da `leerMacro` con `vista: 'comida'`, que es la que ya usa la
    // tarjeta (`MealCard.MealProgressBars`). La celda entera vive en `lib/estadoDelMacro`
    // (`celdaDeLaVentana`) con su prueba: una regla que decide lo que el cliente lee no
    // puede quedarse sin test por vivir dentro de un componente de dos mil líneas.
    //
    // LO QUE NO SE VA CON ELLA ES EL COLOR, y es a propósito: esta ventana pinta lo que
    // falta en blanco y el «sobran» en ROJO sólo cuando de verdad se excede (`seExcede`),
    // y `leerMacro` no distingue eso -- para ella todo lo pasado es el naranja de la casa.
    // Así que el estado lo da la regla y el color lo sigue poniendo esta pantalla.
    //
    // LA FRACCIÓN DESDE EL PRIMER SEGUNDO (Jesús, 10-09, regla 1 del bloque 1). «Hoy pone
    // "48 g · 10 g · 15 g", que es sólo el objetivo. Tiene que poner "0 de 48 · faltan
    // 48".» Con la comida vacía esto devolvía el objetivo pelado y NINGÚN estado; ahora la
    // celda se arma igual esté vacía o no, y el cero es un cero de verdad, no un hueco.
    // ─────────────────────────────────────────────────────────────────────────────────
    const macroCell = (servedVal, tgtVal, key) => {
        const c = celdaDeLaVentana({ hay: servedVal, objetivo: tgtVal, key, esPeri: isPeriMode });
        return { num: c.num, de: c.de, status: c.palabra, cls: c.clase, over: c.enRojo };
    };

    // EL 80 Y EL 100 (Jesús, 10-09, y es «lo único del flujo que no existe hoy»).
    //
    //     «Los dos botones salen mientras ninguno de los dos -- proteína e hidratos -- haya
    //      pasado del 80 % de su objetivo en esa comida. En cuanto uno cualquiera de los dos
    //      lo pasa, el 80 desaparece y queda solo el 100.»
    //
    // No depende de la categoría ni del alimento, sólo del estado de la comida: si baja la
    // cantidad y vuelve por debajo del 80 %, vuelven los dos. Y un objetivo de cero cuenta
    // como cruzado, que es la misma regla con la que el motor decide las fases.
    //
    // Para qué sirve: «para dejarle sitio a un segundo alimento. Tus comidas llevan cuatro de
    // media y el principal aporta el 80 % de la proteína: el botón es esa costumbre, puesta
    // en la pantalla».
    //
    // La regla del 80 vive en `lib/elOchenta`, con su prueba: aquí estaba escrita a mano y
    // una regla que decide qué botón se ve no puede quedarse sin test por vivir dentro de
    // un componente de dos mil líneas.


    // EL VERDE Y EL AVISO DE PASARSE (Jesús, 10-09, estados 3 y 4 de su maqueta).
    //
    // El verde: «✓ Esta comida ya es válida. Puedes dejarla así o continuar» -- y NO cierra
    // la puerta: «las categorías siguen ahí y puede seguir metiendo cosas», así que es un
    // aviso y no un final.
    //
    // El de pasarse: «⚠ Te has pasado de hidratos», con el macro nombrado y cuánto sobra, y
    // EN EL MOMENTO: «puede pasar tanto al añadir como al subir la cantidad de algo que ya
    // tenía», no al cerrar. Como sale de `served`, que se recalcula con cada cambio, aparece
    // solo en los dos casos.
    const NOMBRE_DEL_MACRO = { P: 'proteína', H: 'hidratos', G: 'grasa' };
    const estadoDeLaComida = (() => {
        const algo = (served.P > 0) || (served.H > 0) || (served.G > 0);
        if (!algo) return null;
        const macros = isPeriMode ? ['P', 'H'] : ['P', 'H', 'G'];
        // Primero lo que se pasa, que manda sobre todo lo demás.
        for (const k of macros) {
            const sobra = served[k] - target[k];
            if (target[k] > 0 && sobra > MARGEN_VALIDO) {
                return { tipo: 'pasado', macro: k, sobra: Math.round(sobra * 10) / 10 };
            }
        }
        // Y válida es que los tres estén dentro de margen.
        const dentro = macros.every(k => Math.abs(target[k] - served[k]) <= MARGEN_VALIDO);
        return dentro ? { tipo: 'valida' } : null;
    })();
    // DE QUÉ SE QUEDA CORTA, para el reparo al guardar. El macro que más lejos se queda
    // POR DEBAJO, en el orden de la casa (proteína, hidratos, grasa) cuando empatan.
    const seQuedaCortaDe = (() => {
        if (estadoDeLaComida?.tipo === 'valida') return null;
        const macros = isPeriMode ? ['P', 'H'] : ['P', 'H', 'G'];
        let peor = null;
        for (const k of macros) {
            const falta = target[k] - served[k];
            if (target[k] > 0 && falta > MARGEN_VALIDO && (!peor || falta > peor.falta)) {
                peor = { macro: k, falta };
            }
        }
        return peor;
    })();

    const mostrarOchenta = !isManual && !isPeriMode
        && !cruzoElOchenta(served.P, target.P) && !cruzoElOchenta(served.H, target.H);

    const isCuadrada = Math.abs(target.P - served.P) <= 0 &&
                       Math.abs(target.H - served.H) <= 0 &&
                       (isPeriMode || Math.abs(target.G - served.G) <= 0);

    // Calma: a meal that is already valida (within margenValido) has no remaining to fill, so
    // the engine returns no suggestions and you can't keep stuffing it. Block a food that would
    // overshoot the meal target by more than the 4 g margin.
    const getBlockReason = (macrosEf) => {
        if (isPeriMode) return null;
        const margin = 4;
        if (macrosEf.P > 0 && served.P + macrosEf.P > target.P + margin) {
            return 'No cabe - superaría la proteína objetivo de esta comida.';
        }
        if (macrosEf.H > 0 && served.H + macrosEf.H > target.H + margin) {
            return 'No cabe - superaría los hidratos objetivo de esta comida.';
        }
        if (macrosEf.G > 0 && served.G + macrosEf.G > target.G + margin) {
            return 'No cabe - superaría las grasas objetivo de esta comida.';
        }
        return null;
    };

    const preferenceEmojis = {
        grasas_buenas: '🥑', grasas_todo: '🫒', aperitivos: '🍟', arroces: '🍚',
        aves: '🍗', barritas: '🍫', bebidas: '☕', isotonicas: '⚡',
        beb_vegetales: '🥤', bolleria: '🥐', cacao: '🍯', casqueria: '🥩',
        cerdo: '🐷', cereales: '🌾', chocolates: '🍫', cocina_esp: '🥘',
        comida_rapida: '🍔', embutidos: '🥓', fruta: '🍎', helados: '🍦',
        huevos: '🥚', lacteos: '🧀', legumbres: '🫘', carnes_blancas: '🍖',
        carnes_rojas: '🥩', panes: '🍞', pasta: '🍝', pescados: '🐟',
        pizza: '🍕', proteina_polvo: '💪', proteina_vegetal: '🌱', salsas: '🥫',
        sopas: '🍲', superalimentos: '✨', tuberculos: '🥔', vacuno: '🥩', verduras: '🥬'
    };

    // Calma: filtrosSinAlergias removes chips whose category code is in user's avoid list
    // Chips whose id (or mapped preference id) is in avoidedCategories are hidden
    const CHIP_PREF_MAP = {
        'vegetal': ['proteina_vegetal'],
        'otras_carnes': ['carnes_blancas', 'carnes_rojas'],
    };
    const filterAvoided = (chips) => {
        if (!avoidedCategories || avoidedCategories.length === 0) return chips;
        const avoidSet = new Set(avoidedCategories);
        return chips.filter(chip => {
            const prefIds = CHIP_PREF_MAP[chip.id] || [chip.id];
            return !prefIds.some(id => avoidSet.has(id));
        });
    };

    const getCurrentCategories = () => {
        let base = [];
        if (isManual) {
            // Manual: no progression, so expose the WHOLE Calma category universe at once
            // (PREFERENCE_CATEGORIES = the 38 canonical category codes) for free browsing.
            // Order within a category is alphabetical.
            base = filterAvoided(PREFERENCE_CATEGORIES.map(cat => ({ ...cat, value: cat.id })));
            return conNombreCorto([FREQUENT_CATEGORY, ...base]);
        }
        if (isIntraMode) {
            base = INTRA_CATEGORIES;
        } else if (isPostMode) {
            // Calma: post is a single phase - all 12 categoriasPostentreno at once (no split).
            base = POST_CATEGORIES;
        } else if (paso === 1) {
            base = filterAvoided(PROTEIN_CATEGORIES);
        } else if (paso === 2) {
            base = filterAvoided(SIDE_CATEGORIES);
        } else if (paso === 3) {
            // Calma paso 3 (todos) = filtrosDePreferencias: the user's preferences shown in the
            // CANONICAL categoriasParaPreferencias order (the prefs form builds the Set by iterating
            // that fixed list, so [...preferencias] is code order, NOT click order). grasas_buenas
            // (42) is a "fija" -> always present. PREFERENCE_CATEGORIES already IS that canonical
            // order with grasas_buenas first, so just filter it (don't re-order by selection).
            const prefSet = new Set(userPreferences || []);
            base = filterAvoided(
                PREFERENCE_CATEGORIES
                    .filter(c => c.id === 'grasas_buenas' || prefSet.has(c.id))
                    .map(cat => ({ ...cat, value: cat.id }))
            );
        } else {
            base = SIDE_CATEGORIES;
        }
        return conNombreCorto([FREQUENT_CATEGORY, ...base]);
    };

    useEffect(() => {
        if (open && mealKey) {
            // Precargar los alimentos ya guardados de la comida, para que al reabrir el modal
            // se sigan viendo (y se puedan editar/quitar) en lugar de empezar vacío.
            const yaGuardados = (mealsData[mealKey]?.alimentos || []).map(f => ({ ...f }));
            setTempFoods(yaGuardados);
            // La barra de «añadidos» arranca ABIERTA (Francisco, 27-09: «ese desplegable lo quería
            // abierto predeterminadamente», que es como lee el «así se ven desplegados los
            // alimentos que haya» de Jesús del 26-09). Revierte el «cerrada» del 15-08 (fallo 30:
            // abierta se comía media ventana): manda lo más reciente. Se recoge a mano.
            setAddedOpen(true);
            // Calma: intra has its category chips hidden and pre-activated (c(e, true)),
            // so foods load immediately. Auto-select the intra categories on open.
            //
            // Y FUERA DEL PERI, LOS FRECUENTES DE ESA COMIDA (Jesús, 10-09, regla 4: «por
            // defecto, los frecuentes de esa comida»). Abría con la lista vacía y un
            // «selecciona una categoría»: lo primero que veía el cliente al ir a montar su
            // comida era un hueco. OJO: esto es sólo AL ABRIR. Al pasar de fase se sigue
            // dejando en blanco (más abajo), que es lo que él pide para el paso 2: «se abren
            // todas las categorías y ninguna queda marcada: tiene que elegir». Al RETROCEDER
            // vuelven los frecuentes, que es lo del 10-09.
            // Sin los frecuentes marcados (24-09): se abre sin categoría y con los filtros a
            // la vista, que sin categoría no hay lista que enseñar.
            // SIN FILTRO, TAMPOCO FRECUENTES, Y LAS CATEGORÍAS A LA VISTA PARA ELEGIR (vídeo de
            // Jesús del 27-09: «vas a encontrarlo sin filtro, así, como estaba la otra aplicación,
            // y es, tú eliges la categoría que quieres… no sé por qué salen los frecuentes, no
            // debería activarse»; Francisco, 27-09: «sin Frecuentes, eliges categoría»). Revierte
            // los Frecuentes marcados y los filtros recogidos del 26-09 (07abb7ce).
            setSelectedCategories(isIntraMode ? INTRA_CATEGORIES.map(c => ({ ...c, value: c.id })) : []);
            setFiltrosAbiertos(!isIntraMode);
            setHaTocadoCategoria(false);
            setVerTodos(false);
            setCategoryFoods([]);
            setSearchQuery('');
            setSearchResults([]);
            setIsSearching(false);
            setSelectedFood(null);
            setSelectedPreparations([]);
            setAvailablePreps([]);

            // Determine correct starting paso based on already-saved foods in this meal
            if (!isPeriMode) {
                const ex = (mealsData[mealKey]?.alimentos || []).reduce((acc, f) => ({
                    P: acc.P + (f.macros_efectivos?.P || 0),
                    H: acc.H + (f.macros_efectivos?.H || 0),
                }), { P: 0, H: 0 });
                const t = mealKey ? getMealTarget(mealKey) : { P: 0, H: 0 };
                // LLEGAR AL 80 % YA ES CRUZARLO, igual que el letrero y el botón 80 (ver abajo).
                const pOk = cruzoElOchenta(ex.P, t.P);
                const hOk = cruzoElOchenta(ex.H, t.H);
                setPaso(!pOk ? 1 : (!hOk ? 2 : 3));
            } else {
                setPaso(1);
            }
        }
    }, [open, mealKey, mode]); // eslint-disable-line

    // Switching mode (manual<->auto) mid-modal resets the category selection so stale chips
    // don't linger. Skip the first run (ref starts equal) so intra's auto-selection survives mount.
    const prevManual = useRef(isManual);
    useEffect(() => {
        if (prevManual.current === isManual) return;
        prevManual.current = isManual;
        if (!open) return;
        setSelectedCategories([]);
        setCategoryFoods([]);
        setSelectedPreparations([]);
        setAvailablePreps([]);
    }, [isManual, open]);

    // Calma filtroParaAplicar is REACTIVE: the phase is recomputed from the current served macros
    // every change - NOT a one-way advance. So removing the food that supplied the carbs drops you
    // back from paso 3 to paso 2. haySuficientes = cruzoElOchenta (llegar al 80 % ya cuenta; Calma usaba > 0.8,
    // STRICT >). target==0 -> that macro counts as sufficient (nothing to add there).
    useEffect(() => {
        if (!open) return;       // modal stays mounted while closed; don't react to day switches
        if (isPeriMode) return;  // peri (intra/post) is a single phase - no paso split
        if (isManual) return;    // manual: no 80% progression at all
        // LA FASE CRUZA CON LA MISMA REGLA QUE EL LETRERO Y EL BOTÓN 80 (Jesús, 24-09: con el
        // flan proteico la línea decía «lo suyo sería añadir los hidratos» y las categorías
        // seguían siendo de proteína). Aquí quedaba el «mayor que» estricto de Calma, y el
        // letrero y el 80 ya eran «mayor o igual» desde el 12-09: justo en el 80 % el letrero
        // pasaba a los hidratos y la fase no. Ahora las tres cosas miran `cruzoElOchenta`.
        const pOk = cruzoElOchenta(served.P, target.P);
        const hOk = cruzoElOchenta(served.H, target.H);
        const newPaso = !pOk ? 1 : (!hOk ? 2 : 3);
        if (newPaso !== paso) {
            setPaso(newPaso);
            // HACIA DELANTE, EN BLANCO; HACIA ATRÁS, LOS FRECUENTES.
            //
            // Al AVANZAR se sigue dejando sin marcar, que es lo que pidió para el paso 2:
            // «se abren todas las categorías y ninguna queda marcada: tiene que elegir».
            //
            // Al VOLVER ATRÁS no, y esto es del 10-09: «si quiere más proteína después de
            // haber cruzado, baja la cantidad de lo que puso y le vuelven a salir». Bajaba
            // la cantidad y lo que le salía era «Selecciona una categoría arriba para ver
            // los alimentos»: una pantalla vacía, sin ningún alimento donde volver a pulsar
            // el 80 y el 100 que acababan de reaparecer. Los dos documentos no se pisan --
            // uno habla de avanzar y el otro de retroceder --, así que cada uno manda en su
            // dirección.
            // Y HACIA ATRÁS TAMPOCO SE MARCAN LOS FRECUENTES (24-09, Francisco: que no
            // vengan preseleccionados). Se abren los filtros en los dos sentidos.
            // Y al cambiar de fase, lo mismo que al abrir (27-09): sin Frecuentes y a elegir.
            setSelectedCategories([]);
            setFiltrosAbiertos(true);
            // Y SI AVANZA, SE ABREN LOS FILTROS: se desmarca todo para que elija (lo de arriba),
            // y con la barra recogida eso era una lista vacía sin categorías a la vista.
            setCategoryFoods([]);
            setSelectedPreparations([]);
            setAvailablePreps([]);
            // SIN AVISOS AL PASAR DE FASE (Jesús, reunión del 25-09: «Macros cubiertos, últimos
            // toques: fuera. Eso que salta de color azul fuera; ese tipo de avisos sobran»). El
            // letrero de arriba ya dice qué toca.
        }
    }, [open, served.P, served.H, target.P, target.H, paso, isPeriMode, isManual]);

    // Calma: filtrosActivacionPorDefecto = false. In paso 3 (cuadrarMacros / "últimos
    // toques") the preference category chips are shown but start INACTIVE - the user
    // picks which to finish with. We previously auto-selected all of them, which was a
    // bug (every chip appeared selected and the whole catalog loaded). No pre-activation.

    // Reload foods when macros, categories or preparations change
    useEffect(() => {
        if (!open) return;
        let cancelled = false;
        // ── «FRECUENTES» SE SUMA, NO MANDA (Francisco, 21-09) ──────────────────
        //
        // «Si selecciono todas las categorías no muestra nada, o sólo muestra lo que tengo en
        // alimentos frecuentes; si desmarco alimentos frecuentes, ahí sí me aparece el resto.»
        //
        // Aquí estaba: en cuanto «frecuentes» entraba en la selección, esta rama se llevaba la
        // búsqueda entera y las demás categorías marcadas ni se miraban. Y a quien todavía no
        // tiene frecuentes -- la pantalla se lo dice: «aún no tienes alimentos frecuentes» --
        // le dejaba la lista VACÍA con seis categorías marcadas.
        //
        // La selección es múltiple, así que se comporta como múltiple: se piden las dos cosas
        // y se juntan, los frecuentes delante (que para eso se marcan) y sin repetir a nadie.
        const hasFrequent = selectedCategories.some(c => c.id === '__frequent__');
        const otrasCategorias = selectedCategories.filter(c => c.id !== '__frequent__');
        if (hasFrequent && otrasCategorias.length > 0) {
            setLoadingFoods(true);
            const frecuentes = new URLSearchParams({ frequent: 'true', limit: '20',
                ...(mealKey ? { meal_key: mealKey } : {}), ...getDiaParams() });
            Object.entries(getMacrosParams()).forEach(([k, v]) => frecuentes.set(k, v));

            const porCategoria = new URLSearchParams({
                q: '', category: otrasCategorias.flatMap(c => c.prefixes || []).filter(Boolean).join(','),
                limit: '100', ...getDiaParams() });
            if (!isManual) {
                if (target.P > 0) porCategoria.set('p_rest', Math.max(0, remaining.P));
                if (target.H > 0) porCategoria.set('h_rest', Math.max(0, remaining.H));
                if (target.G > 0) porCategoria.set('g_rest', Math.max(0, remaining.G));
                if (isIntraMode || isPostMode) {
                    porCategoria.set('peri', isIntraMode ? 'intra' : 'post');
                    porCategoria.set('g_rest', target.G - served.G + MARGEN_VALIDO);
                }
                const sufP = cruzoElOchenta(served.P, target.P);
                const sufH = cruzoElOchenta(served.H, target.H);
                if ((sufP && sufH) || paso === 3) porCategoria.set('cuadrar', 'true');
            }
            if (selectedPreparations.length > 0) porCategoria.set('tag', selectedPreparations.join(','));

            Promise.all([
                api(`/api/calculator/search?${frecuentes}`).catch(() => ({})),
                api(`/api/calculator/search?${porCategoria}`).catch(() => ({})),
            ]).then(([f, c]) => {
                if (cancelled) return;
                const vistos = new Set();
                const juntos = [];
                for (const a of [...(f.alimentos || []), ...(c.alimentos || [])]) {
                    // SIN ID TAMBIÉN ENTRA. Descartando los que no lo traen se perdían dos
                    // alimentos de cada ochenta y cuatro: no son duplicados, son alimentos
                    // que llegan sin id y que en la lista de la categoría sí salían.
                    const id = a?.id ?? a?.alimento_id ?? null;
                    if (id !== null) {
                        if (vistos.has(id)) continue;
                        vistos.add(id);
                    }
                    juntos.push(a);
                }
                setCategoryFoods(juntos);
                // Las preparaciones que se pueden filtrar salen de lo que hay en la lista, así
                // que también se juntan: si no, marcar «frecuentes» escondía las de la categoría.
                setAvailablePreps([...new Set([...(f.available_preps || []),
                                               ...(c.available_preps || [])])]);
            }).finally(() => { if (!cancelled) setLoadingFoods(false); });
        } else if (hasFrequent) {
            setLoadingFoods(true);
            // Calma: frequent foods (top-20 by raw count) go through the SAME engine -
            // suggested quantity + macro rule + diferencia ordering, using the meal remaining.
            // DE ESA COMIDA (Jesús, 10-09, regla 4): «los veinte que más repite EN ESA
            // comida». Sin `meal_key` el servidor cuenta el día entero y al abrir la
            // Comida 1 le salían sus alimentos de la cena.
            const params = new URLSearchParams({ frequent: 'true', limit: '20',
                ...(mealKey ? { meal_key: mealKey } : {}), ...getDiaParams() });
            const mp = getMacrosParams();
            Object.entries(mp).forEach(([k, v]) => params.set(k, v));
            api(`/api/calculator/search?${params}`).then(result => {
                if (!cancelled) { setCategoryFoods(result.alimentos || []); setAvailablePreps(result.available_preps || []); }
            }).catch(() => {}).finally(() => { if (!cancelled) setLoadingFoods(false); });
        } else if (selectedCategories.length > 0) {
            setLoadingFoods(true);
            const categoryParam = selectedCategories.flatMap(c => c.prefixes || []).filter(Boolean).join(',');
            const params = new URLSearchParams({ q: '', category: categoryParam, limit: '100', ...getDiaParams() });
            // Manual: send NO macro context, so the backend returns the plain category list
            // (no suggested quantity, no diferencia ordering). Frontend sorts it alphabetically.
            if (!isManual) {
                // Calma uses ONE unified remaining (full meal target minus added), all three macros,
                // every step. The "paso" only changes which CATEGORIES are shown, never the macro
                // constraints. Sending only P+G in paso 1 left H unconstrained -> Tortitas 4ud and an
                // order that ignored hidratos. Send all three, no tolerance.
                if (target.P > 0) params.set('p_rest', Math.max(0, remaining.P));
                if (target.H > 0) params.set('h_rest', Math.max(0, remaining.H));
                if (target.G > 0) params.set('g_rest', Math.max(0, remaining.G));
                // Calma prioridad fase (Dieta.js ordenarIngredientesPorMacro): once P AND H are >80%
                // of target (porcentajeSuficienteMacros) the sort switches to `cuadrarMacros` (good
                // fats) REGARDLESS of meal type; otherwise peri uses its own list. Peri meals always
                // add margenValido to the fat budget (g_rest), independent of the fase.
                const sufP = cruzoElOchenta(served.P, target.P);
                const sufH = cruzoElOchenta(served.H, target.H);
                if (isIntraMode || isPostMode) {
                    // peri = MEAL TYPE (post → cat-25 universe + grasas margin); sent always.
                    params.set('peri', isIntraMode ? 'intra' : 'post');
                    params.set('g_rest', target.G - served.G + MARGEN_VALIDO);
                }
                // cuadrar = prioridad FASE (good fats), once P&H >80% - independent of meal type.
                if (sufP && sufH || paso === 3) params.set('cuadrar', 'true');
            }
            if (selectedPreparations.length > 0) params.set('tag', selectedPreparations.join(','));
            api(`/api/calculator/search?${params}`).then(result => {
                if (!cancelled) {
                    setCategoryFoods(result.alimentos || []);
                    setAvailablePreps(result.available_preps || []);
                }
            }).catch(() => {}).finally(() => { if (!cancelled) setLoadingFoods(false); });
        }
        // AQUÍ SE RELANZABA LA BÚSQUEDA POR TEXTO cada vez que cambiaba lo que le queda a la
        // comida, con el hueco de macros dentro. Era la otra mitad del fallo 3: la lista de
        // resultados se reordenaba sola -- o se vaciaba -- sin que nadie hubiera tocado el
        // buscador. Lo que se ha escrito no depende de los macros, así que ya no se relanza.
        return () => { cancelled = true; };
    }, [remaining.P, remaining.H, remaining.G, selectedCategories, selectedPreparations, isManual]); // eslint-disable-line

    /**
     * LO QUE LLEVA EL DÍA DE CADA FAMILIA CALIBRADA, PARA QUE LOS NÚMEROS NO CAMBIEN AL
     * GUARDAR (punto 135 del 26-08).
     *
     * Jesús: «añades 100 g de almendras y la proteína no se mueve; al guardar, pasa a contar
     * los 23 g». Reproducido con 25 g: aquí ponía «P 0 · H 0 · G 13» y la comida guardada
     * decía «2,9 P». Esta ventana calculaba con el motor de Calma, que es el de antes de que
     * existiera la calibración progresiva; la comida, con la calibración.
     *
     * La cuenta tiene tres sumandos y hacen falta los tres, porque `tempFoods` arranca
     * PRECARGADO con lo que esta comida ya tenía guardado y el acumulado del día también lo
     * cuenta: sumarlos sin más lo contaba dos veces, y con 25 g de almendras guardadas el
     * tramo saltaba al lleno antes de tiempo. Se descuenta lo que la comida tenía y se suma
     * lo que tiene ahora, así valen igual añadir, quitar y cambiar la cantidad aquí dentro.
     *
     * El bloque de cada alimento lo dice el servidor, para no repetir aquí la clasificación
     * por categorías.
     */
    const getDiaParams = () => {
        const gramosDe = (lista, bloque) => (lista || [])
            .filter((f) => f.bloque === bloque)
            .reduce((s, f) => s + (Number(f.cantidad_g) || 0), 0);
        const guardados = mealsData[mealKey]?.alimentos || [];
        const delDia = (bloque, acum) => Math.max(0,
            (acum || 0) - gramosDe(guardados, bloque) + gramosDe(tempFoods, bloque));
        return {
            dia_cp: delDia('cereal_pan', acumFamilias?.cereal_pan?.gramos),
            dia_fs: delDia('fruto_seco', acumFamilias?.fruto_seco?.gramos),
        };
    };

    /**
     * LA VENTANA LE PREGUNTA AL MISMO MOTOR QUE LA COMIDA (punto 135, la parte de fondo).
     *
     * Aquí se calculaban los macros de tres maneras distintas -- el sugeridor al listar, la
     * regla de categoría al añadir y una regla de tres al cambiar la cantidad -- y ninguna
     * era la del día. Por eso los números cambiaban al guardar.
     *
     * En vez de repetir la calibración aquí, se le pide al servidor que calibre el día
     * ENTERO con esta comida tal y como está en la ventana. Es la misma llamada que ya usa
     * Nutrición, así que por definición da lo mismo que quedará guardado, y cierra de paso
     * los casos que el cálculo al añadir no puede ver: subir la cantidad hasta cruzar de
     * tramo, quitar un alimento y que los demás bajen, o que dos frutos secos se sumen.
     *
     * Con retardo, porque los botones de cantidad se pulsan seguidos. Y si el servidor
     * devuelve lo mismo que ya había, se conserva el array anterior: si se creara uno nuevo
     * cada vez, este mismo efecto se volvería a disparar y no pararía nunca.
     */
    useEffect(() => {
        if (!open || !mealKey || tempFoods.length === 0) return undefined;
        const t = setTimeout(async () => {
            try {
                // EL MISMO DÍA QUE PREGUNTA NUTRICIÓN, NI UNA COMIDA MÁS (Francisco,
                // 13-09-2026: «"cuadrado" en la tarjeta y "Válido" en el diálogo»).
                //
                // Esto mandaba `mealsData` ENTERO, y `mealsData` se queda con todas las
                // comidas que alguna vez tuvieron alimentos, también las que el reparto de
                // ahora ya no enseña (ver `comidasDelReparto` en NutritionPage: el día de 4
                // comidas que pasa a 3 deja una «Comida 4» invisible). Nutrición pregunta
                // sólo por las del reparto. Dos días distintos, y el tramo de la
                // calibración lo decide el TOTAL DEL DÍA de cada familia: con 30 g de
                // almendras a la vista y otros 30 escondidos, la tarjeta contaba su
                // proteína al 50 % y esta ventana al 100 %. La misma comida, 66,7 en la
                // tarjeta («cuadrado») y 70,1 aquí («válido +3,4»).
                //
                // Y con `meal_order`, que es lo otro que mandaba Nutrición y aquí faltaba.
                const delReparto = (mealOrder && mealOrder.length)
                    ? mealOrder
                    : Object.keys(mealsData || {});
                const comidas = {};
                delReparto.forEach((k) => {
                    comidas[k] = ((mealsData || {})[k]?.alimentos || []).map((f) => ({
                        alimento_id: f.alimento_id, cantidad_g: f.cantidad_g,
                    }));
                });
                comidas[mealKey] = tempFoods.map((f) => ({
                    alimento_id: f.alimento_id, cantidad_g: f.cantidad_g,
                }));
                const res = await api('/api/calculator/calibrar-dia', {
                    method: 'POST', body: JSON.stringify({ meal_order: delReparto, comidas }),
                });
                const fila = res?.comidas?.[mealKey] || [];
                setTempFoods((prev) => {
                    let cambio = false;
                    const next = prev.map((f, i) => {
                        const ef = fila[i]?.macros_efectivos;
                        if (!ef) return f;
                        const a = f.macros_efectivos || {};
                        const bloque = fila[i].bloque ?? null;
                        if (a.P === ef.P && a.H === ef.H && a.G === ef.G && (f.bloque ?? null) === bloque) return f;
                        cambio = true;
                        return { ...f, macros_efectivos: ef, bloque };
                    });
                    return cambio ? next : prev;
                });
            } catch (e) {
                // Si no llega, se quedan los macros estimados: el guardado los cuadra igual.
                console.error('[calibrar-dia en la ventana]', e);
            }
        }, 350);
        return () => clearTimeout(t);
    }, [tempFoods, open, mealKey]); // eslint-disable-line

    const getMacrosParams = () => {
        // Calma's manual builder uses ONE unified remaining (full meal target minus
        // added ingredients), all three macros, NO tolerance. The engine (calma_suggest)
        // computes quantity = floor(min(remaining/perUnit)) and orders by diferenciaDeMacros.
        const params = {};
        if (isManual) return params;   // manual: no suggestion/quantity context
        if (target.P > 0) params.p_rest = Math.max(0, remaining.P);
        if (target.H > 0) params.h_rest = Math.max(0, remaining.H);
        if (target.G > 0) params.g_rest = Math.max(0, remaining.G);
        // Calma prioridad fase: cuadrarMacros once P&H >80% of target (any meal); else peri uses
        // its own list. Peri meals always add margenValido to the fat budget (g_rest).
        const sufP = cruzoElOchenta(served.P, target.P);
        const sufH = cruzoElOchenta(served.H, target.H);
        if (isIntraMode || isPostMode) {
            params.peri = isIntraMode ? 'intra' : 'post';   // meal type: cat-25 universe + grasas margin
            params.g_rest = target.G - served.G + MARGEN_VALIDO;
        }
        if (sufP && sufH || paso === 3) params.cuadrar = 'true';   // prioridad fase (good fats)
        return params;
    };


    const handleCategoriesChange = (ids) => {
        setHaTocadoCategoria(true);
        // AL ELEGIR CATEGORÍA SE RECOGE EL PANEL, OTRA VEZ (Jesús, 27-09 21:45: «cuando yo toque
        // la categoría, no quiero que se despliegue la forma de preparación; tiene que estar
        // recogida»). Por la tarde Francisco había pedido lo contrario («deja que el usuario
        // decida»); manda lo más reciente. La preparación se elige tocando la barra.
        if (ids.length) setFiltrosAbiertos(false);
        setVerTodos(false);
        setSelectedCategories(getCurrentCategories().filter(c => ids.includes(c.id)));
        setCategoryFoods([]);
        setSelectedPreparations([]);
    };

    const handleCategoryClick = async (category) => {
        setSelectedCategory(category);
        setSelectedPreparation('');
        setLoadingFoods(true);
        setCategoryFoods([]);
        setAvailablePreps([]);

        try {
            if (category.id === '__frequent__') {
                // Frequent foods through the same engine (quantity + macro rule + diferencia).
                const params = new URLSearchParams({ frequent: 'true', limit: '20', ...getMacrosParams(), ...getDiaParams() });
                const result = await api(`/api/calculator/search?${params}`);
                setCategoryFoods(result.alimentos || []);
                setAvailablePreps(result.available_preps || []);
            } else {
                const params = new URLSearchParams({
                    q: '',
                    category: category.prefixes[0],
                    limit: '100',
                    ...getMacrosParams(),
                    ...getDiaParams(),
                });
                const result = await api(`/api/calculator/search?${params}`);
                setCategoryFoods(result.alimentos || []);
                setAvailablePreps(result.available_preps || []);
            }
        } catch (err) {
            console.error('Error cargando alimentos:', err);
            toast.error('Error cargando alimentos');
        } finally {
            setLoadingFoods(false);
        }
    };

    const handleBackToCategories = () => {
        setSelectedCategory(null);
        setCategoryFoods([]);
    };

    /**
     * BUSCAR POR NOMBRE, COMO EN CALMA: ESTA LISTA TIENE QUE SALIR IGUAL QUE LA SUYA.
     *
     * Las tres cosas que hace el buscador de la Dieta de Calma, y las tres están aquí:
     *
     *  1. FILTRA por nombre: cada palabra escrita tiene que estar en el nombre
     *     (`Ye(nombre, texto.split(" "), true)`), sin buscar parecidos. Eso lo hace el
     *     servidor (`buscar_alimentos`) y es lo que cierra el fallo 3 de Jesús (15-08):
     *     buscando «pechuga» salen 58 pechugas y ninguna Pepsi, porque ninguna Pepsi lleva
     *     esa palabra. No hace falta nada más para que mande lo que ha escrito el cliente.
     *  2. PONE CANTIDAD con el hueco de macros de la comida (`ajustarCantidadIngrediente`).
     *     Sin el hueco el alimento entraba con su ración: buscando «leche de almendras»
     *     tapaba 1 g de los 10 g de grasa que faltaban (Francisco, 17-08). Por eso el hueco
     *     viaja con `solo_cantidad`: pone la cantidad y NO tira a nadie de la lista.
     *  3. ORDENA por diferencia de macros y desempata por nombre, y nada más. No puntúa el
     *     parecido con lo escrito: ese scoring (`Ie`) es del catálogo de «Alimentos», que es
     *     otra pantalla. Ordenar aquí por parecido partía la lista en bloques que Calma
     *     mezcla por encaje, y era la lista que no cuadraba con la suya (Francisco, 17-08).
     *
     * `peri` se mantiene porque no es un hueco de macros: dice de qué alimentos se compone
     * un intra o un post.
     */
    const handleSearch = async (query) => {
        setSearchQuery(query);
        const texto = query.trim();

        if (texto.length < 2) {
            turnoBusqueda.current += 1;   // invalida lo que estuviera en vuelo
            setIsSearching(false);
            setSearchResults([]);
            return;
        }

        setIsSearching(true);
        setLoadingFoods(true);
        const miTurno = ++turnoBusqueda.current;

        try {
            const macrosParams = getMacrosParams();
            const params = new URLSearchParams({ q: texto, limit: '200', ...macrosParams, ...getDiaParams() });
            // LOS CHIPS ENCENDIDOS TAMBIÉN FILTRAN AL ESCRIBIR (31-08-2026).
            //
            // Aquí se mandaba solo `q`. Los chips seguían pintados y encendidos, pero en
            // cuanto tecleabas dejaban de existir: un cliente puso «Genérico», buscó «pechuga
            // de pollo» y le salieron todas las marcas. Un filtro que se ve encendido y no
            // filtra es peor que no tenerlo, porque el cliente cree que la lista ya está
            // filtrada.
            // Y DESDE EL 24-09 LAS CATEGORÍAS NO (Francisco: «que el buscador de alimentos no
            // esté atado a las categorías, que funcione independiente de las categorías
            // seleccionadas»). Lo escrito busca en todo el catálogo. Las preparaciones
            // tampoco: cuelgan de la categoría y mientras se escribe los filtros no se ven,
            // así que recortarían la búsqueda sin que se sepa por qué. Manda lo más reciente
            // sobre lo del 31-08. El intra y el post siguen con lo suyo (`peri`), como Calma.
            if (Object.keys(macrosParams).length > 0) params.set('solo_cantidad', 'true');
            if (isIntraMode || isPostMode) params.set('peri', isIntraMode ? 'intra' : 'post');
            const result = await api(`/api/calculator/search?${params}`);
            if (miTurno !== turnoBusqueda.current) return;   // ya se ha escrito otra cosa
            // EL ORDEN VIENE DEL SERVIDOR Y ES EL DE CALMA (diferencia de macros). Reordenar
            // aquí por parecido con lo escrito -- el scoring `Ie`, que es el del catálogo de
            // Alimentos, no el de la Dieta -- dejaba una lista distinta a la de Calma.
            setSearchResults(result.alimentos || []);
            setFueraPorPasarse(result.fuera_por_pasarse || 0);
        } catch (err) {
            console.error('Error buscando:', err);
            if (miTurno === turnoBusqueda.current) {
                toast.error('No hemos podido buscar ahora mismo. Inténtalo otra vez.');
            }
        } finally {
            if (miTurno === turnoBusqueda.current) setLoadingFoods(false);
        }
    };

    // Y SI TOCAS UN CHIP CON ALGO ESCRITO, SE VUELVE A BUSCAR (31-08-2026). La búsqueda solo
    // se lanza al teclear, así que sin esto encender «Genérico» con «pechuga de pollo» ya
    // escrito no cambiaba la lista: el chip se veía encendido y los resultados eran los de
    // antes. Mismo fallo por otra puerta.
    useEffect(() => {
        if (!open) return;
        const texto = (searchQuery || '').trim();
        if (texto.length < 2) return;
        handleSearch(searchQuery);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedCategories, selectedPreparations]);

    // LA CANTIDAD DEL 80, EN UN SITIO: la que se enseña bajo el botón y la que se añade. Es el
    // 80 % de lo que le FALTA a la comida en el macro que toca (proteína; si ya cruzó, hidratos),
    // aunque la sugerida se pase un pelo del hueco (vídeo de Jesús del 27-09, lib/elOchenta).
    const ochentaDe = (food) => {
        const m = cruzoElOchenta(served.P, target.P) ? 'H' : 'P';
        return cantidadDelOchenta(food._cantidad_sugerida, food._macros_sugeridos?.[m], remaining[m]);
    };

    /** `fraccion` es `EL_OCHENTA` (el botón «80») o 1 (el «100»). Ver `mostrarOchenta`. */
    const handleSelectFood = async (food, fraccion = 1) => {
        try {
            const foodId = food.id || food._id;
            // SOLO LO QUE HAY EN LA HOJA (Jesús, 27-09: «intento meter ciclodextrina y no me deja;
            // me dice que está y no está»). La hoja arranca con lo que ya llevaba la comida
            // (`yaGuardados`) y al guardar la REEMPLAZA; se miraba además la comida guardada, que
            // aún tenía lo que él acababa de quitar con la X, y no dejaba volver a ponerlo.
            const alreadyInTemp = tempFoods.some(f => f.alimento_id === foodId);
            if (alreadyInTemp) {
                toast.error(`${food.nombre} ya está en esta comida - ajusta su cantidad directamente.`);
                return;
            }

            const base = food._cantidad_sugerida || food.racion || 100;
            // EL 80 SE CALCULA SOBRE LA CANTIDAD QUE YA CUADRA EL MACRO (Jesús, 10-09).
            //
            // `_cantidad_sugerida` es la que el motor calcula para cerrar el macro que ese
            // alimento aporta, y ya viene topada por los demás («si la grasa se acaba antes,
            // manda la grasa»). El 80 % de esa cantidad hereda ese tope, así que no puede
            // pasarse de nada. Se redondea a la décima como todo lo demás.
            // HACIA ARRIBA Y NO AL MÁS CERCANO: el 80 % de una cantidad que ya viene corta
            // se quedaba por debajo del listón y el botón no se cumplía a sí mismo.
            const quantity = fraccion === 1 ? base : ochentaDe({ ...food, _cantidad_sugerida: base });
            // En la comida MANUAL no hay hueco que cuadrar, así que el alimento no trae
            // macros calculados y se le piden al servidor: escalar aquí los campos crudos
            // contaría lo que dice la etiqueta y no lo que cuenta el método, y en los
            // alimentos por unidades lo multiplicaría además por la ración equivocada.
            let macrosEf;
            let bloque = food.bloque ?? null;
            // LOS MACROS DEL 80 SON LOS DEL 80 (vídeo de Jesús del 27-09). `_macros_sugeridos`
            // son los de la cantidad ENTERA: con el botón 80 entraba el 80 % de la cantidad
            // contando el 100 % de la proteína, la comida se daba por cumplida y dejaba de
            // ofrecer proteínas («tú ya has cogido la proteína, aquí te tiene que seguir
            // ofreciendo»). Con el 80 se piden al servidor para la cantidad que de verdad entra.
            if (fraccion === 1 && food._macros_sugeridos && Object.keys(food._macros_sugeridos).length > 0) {
                macrosEf = { P: food._macros_sugeridos.P || 0, H: food._macros_sugeridos.H || 0, G: food._macros_sugeridos.G || 0 };
            } else {
                try {
                    const res = await api('/api/calculator/macros-efectivos', {
                        method: 'POST',
                        // Con lo que lleva el día: sin esto la comida manual enseñaba la
                        // proteína de unas almendras a cero y al guardar aparecía (punto 135).
                        body: JSON.stringify({ alimento_id: foodId, cantidad_g: quantity, es_vegano: false,
                                               ...getDiaParams() }),
                    });
                    const ef = res.efectivos || {};
                    macrosEf = { P: ef.P || 0, H: ef.H || 0, G: ef.G || 0 };
                    bloque = res.bloque ?? bloque;
                } catch (e) {
                    console.error('[macros-efectivos]', e);
                    const isUnit = food.por_unidad ?? food.unidades;
                    const factor = isUnit ? (quantity / (food.racion || 100)) : (quantity / 100);
                    macrosEf = {
                        P: Math.round((food.proteinas || 0) * factor * 10) / 10,
                        H: Math.round((food.hidratos || 0) * factor * 10) / 10,
                        G: Math.round((food.grasas || 0) * factor * 10) / 10,
                    };
                }
            }

            // LO QUE EL CLIENTE HA BUSCADO ENTRA (Jesús, 15-08, punto 2 de las mejoras: «que
            // lo que el cliente ha nombrado entre sí o sí»). El bloqueo de Calma -- no dejar
            // meter algo que se pase del objetivo -- se queda para las SUGERENCIAS, que es
            // donde tiene sentido: allí lo propone la app. Si lo ha escrito él, entra y se le
            // dice que se pasa, con la cantidad a mano para bajarla.
            const seVaDeMadre = isManual ? null : getBlockReason(macrosEf);
            if (seVaDeMadre) {
                if (!isSearching) { toast.error(seVaDeMadre); return; }
                toast.warning(`${food.nombre} entra, pero esta comida se pasa. Baja la cantidad si quieres cuadrarla.`);
            }

            // Cuadrar el macro puede pedir una cantidad que no se come: 10 g de grasa con
            // leche de almendras son casi 900 ml. Se pone -- es la cantidad que cuadra --
            // pero se avisa, igual que al teclearla a mano (Jesús, 16-08).
            const avisoTope = avisoRazonable(food, quantity, {
                porUnidad: Boolean(food.por_unidad ?? food.unidades),
                pesoUnidad: food.peso_unidad || food.racion || 0,
            });
            if (avisoTope) toast.warning(avisoTope, { id: 'tope-razonable' });

            const foodToAdd = {
                ...food,
                alimento_id: food.id || food._id,
                cantidad_g: quantity,
                por_unidad: food.por_unidad ?? food.unidades ?? false,
                peso_unidad: food.peso_unidad || food.racion || 100,
                racion: food.racion || 100,
                macros_efectivos: macrosEf,
                // La familia calibrada, para que el siguiente alimento que se busque cuente
                // ya estos gramos en el tramo del día (`getDiaParams`).
                bloque,
            };

            setTempFoods(prev => [...prev, foodToAdd]);
            setSelectedFood(null);
            // AL AÑADIR, FUERA LO ESCRITO Y LAS CATEGORÍAS ABIERTAS (Jesús, reunión del 25-09):
            // «cuando añado un alimento quiero que el desplegable salga desplegado», y «añado
            // uva y me sale "No cabe en lo que le queda a esta comida"; no he buscado nada más,
            // ese mensaje sobra». Era lo mismo: lo escrito se quedaba, la lista se rehacía con
            // la comida ya casi llena y, con texto, las categorías se esconden. REVIERTE la ficha
            // 127 del 5-09 (que la búsqueda se quedara para coger dos cosas): manda lo más
            // reciente.
            setSearchQuery('');
            // Y SE QUEDA TODO LIMPIO, COMO EN LA CALCULADORA DE ANTES (Jesús, 27-09, vídeo y
            // audios de las 21:55 a las 21:58: «cuando ya añado un alimento me sale otra vez
            // marcado el huevo»; «se me carga y se me queda limpio todo otra vez»). Sin
            // categoría ni preparación marcadas y con las categorías a la vista para elegir la
            // siguiente; lo añadido se ve abajo. Revierte el «los filtros se quedan recogidos»
            // del 26-09: manda lo más reciente.
            if (!isPeriMode) {
                setSelectedCategories([]);
                setSelectedPreparations([]);
                setCategoryFoods([]);
                setAvailablePreps([]);
                setFiltrosAbiertos(true);
                setVerTodos(false);
            }
            // LO ESCRITO Y LA LISTA SE QUEDAN (ficha 127 del parte de Gonzalo, 5-09). Aquí se
            // vaciaba la búsqueda al añadir, y quien quería dos cosas de la misma búsqueda
            // -- dos yogures, dos quesos -- tenía que volver a teclearla. El añadido ya está
            // en «añadidos» abajo; la lista sigue donde estaba para coger el siguiente.

        } catch (err) {
            console.error('Error añadiendo alimento:', err);
            toast.error('Error al añadir alimento');
        }
    };

    const handleFoodPreview = (food) => {
        const qty = food._cantidad_sugerida || food.racion || 100;
        const macros = food._macros_sugeridos || {
            P: Math.round((food.proteinas || 0) * qty / 100 * 10) / 10,
            H: Math.round((food.hidratos || 0) * qty / 100 * 10) / 10,
            G: Math.round((food.grasas || 0) * qty / 100 * 10) / 10
        };
        setSelectedFood(food);
        setAdjustedQuantity(qty);
        setAdjustedMacros(macros);
    };

    const handleAdjustQuantity = async (delta) => {
        if (!selectedFood) return;
        const isPorUnidad = selectedFood.por_unidad ?? selectedFood.unidades;
        const unitWeight = selectedFood.peso_unidad || selectedFood.racion || 100;
        const step = isPorUnidad ? unitWeight : 1;
        const newQty = Math.max(step, adjustedQuantity + (delta * step));
        // (El muro de 2.000 g se retiró el 22-09: ver lib/cantidades.)
        // Lo que cabe de ESTE alimento en una comida: se avisa y se deja seguir (16-08).
        const avisoTope = avisoRazonable(selectedFood, newQty, { porUnidad: isPorUnidad, pesoUnidad: unitWeight });
        if (avisoTope) toast.warning(avisoTope, { id: 'tope-razonable' });

        try {
            const result = await api('/api/calculator/macros-efectivos', {
                method: 'POST',
                // CON EL DIA DELANTE (ficha 51 del parte de Gonzalo, 4-09-2026).
                //
                // Su ficha: «la calibracion no se aplica al anadir desde dentro de la comida
                // -- dice "hasta 20 g, nada" y al meter 10 g de almendras la proteina sube».
                // Lo gordo de eso ya se arreglo (el buscador y el anadir mandan `getDiaParams`
                // desde el 5-09), pero ESTA llamada se quedo sin el dia: el +/- de la ficha
                // del alimento preguntaba por el alimento a secas, asi que mientras se subia
                // y se bajaba la cantidad se leian unos macros SIN calibrar, y al anadirlo
                // cambiaban solos. Los numeros que se ven mientras se decide tienen que ser
                // los mismos que van a entrar.
                body: JSON.stringify({ alimento_id: selectedFood.id || selectedFood._id, cantidad_g: newQty, es_vegano: false, ...getDiaParams() })
            });
            const ef = result.efectivos || {};
            // Calma: free to adjust past the target (bars show over-target). No block.
            setAdjustedQuantity(newQty);
            setAdjustedMacros({ P: ef.P || 0, H: ef.H || 0, G: ef.G || 0 });
        } catch {
            setAdjustedQuantity(newQty);
            const factor = newQty / 100;
            setAdjustedMacros({
                P: Math.round((selectedFood.proteinas || 0) * factor * 10) / 10,
                H: Math.round((selectedFood.hidratos || 0) * factor * 10) / 10,
                G: Math.round((selectedFood.grasas || 0) * factor * 10) / 10
            });
        }
    };

    // AQUÍ VIVÍA `handleConfirmAddFood`, Y SE QUITA (Francisco, 13-09, ficha 127 del parte de
    // Gonzalo del 4-09).
    //
    // Era la segunda puerta para añadir un alimento -- la del panel de la ficha con su +/- --
    // y ya no la llama nadie: hoy se añade por `handleSelectFood`, con los botones 80 y 100 de
    // la propia fila. Se queda escrito porque era LA QUE VACIABA LA BÚSQUEDA (`setSearchQuery('')`
    // y la lista detrás), que es justo lo que la ficha 127 no quiere: quien busca «pollo» y
    // quiere dos pollos tenía que volver a teclearlo. `handleSelectFood` ya deja lo escrito y
    // la lista donde estaban, así que dejar aquí una copia muerta que hace lo contrario es
    // sembrar el fallo para el día que alguien vuelva a engancharla a un botón.

    const handleRemoveFood = (index) => {
        setTempFoods(prev => prev.filter((_, i) => i !== index));
    };

    // Recalcula macros efectivos para una cantidad (en gramos) dada.
    /**
     * Al cambiar la cantidad, los macros se escalan y el servidor los confirma.
     *
     * Esto rehacía la cuenta desde los macros de la etiqueta (`f.proteinas * mult`), y esos
     * llegan del servidor ya puestos a cero por la regla de categoría: al pulsar «+» sobre
     * unos anacardos su proteína calibrada se iba a cero. Escalar lo que ya cuenta nunca
     * inventa un cero, y el efecto de abajo lo cuadra con el motor del día en cuanto
     * responde -- que es quien sabe si al subir la cantidad se ha cruzado de tramo.
     */
    // Desde el punto de partida del alimento, no desde el último redondeo (lib/escalarMacros).
    const recalcFoodMacros = (f, newQty) => escalarMacros(f, newQty);

    const handleFoodQuantityChange = (index, delta) => {
        // Calma lets you freely increase/decrease an added food's quantity even past the
        // meal target - the macro bars just show over-target (red). No block.
        setTempFoods(prev => prev.map((f, i) => {
            if (i !== index) return f;
            const isUnit = f.por_unidad ?? f.unidades;
            const racion = f.racion || 100;
            const step = isUnit ? (f.peso_unidad || racion) : 1;
            const currentQty = f.cantidad_g || f.cantidad || 0;
            let newQty = Math.max(step, currentQty + (delta * step));
            // EL POST: la bebida vegetal no pasa de los 4 g de grasa (lib/topeGrasaPost).
            if (isPostMode) {
                const tope = cantidadConTope(prev, index, newQty);
                if (tope.frenada) toast.info(tope.aviso || AVISO_TOPE_GRASA_POST, { id: 'tope-grasa-post' });
                newQty = tope.cantidad;
            }
            // El tope por arriba también con los botones: llegar a 2000 g de 5 en 5 cuesta,
            // pero un alimento por unidades sube de 250 en 250 (fallo 28).
            // Y el tope con sentido humano de ese alimento concreto (16-08): avisa, no corta.
            const aviso = avisoRazonable(f, newQty, { porUnidad: Boolean(isUnit), pesoUnidad: step });
            if (aviso) toast.warning(aviso, { id: 'tope-razonable' });
            return recalcFoodMacros(f, newQty);
        }));
    };

    /**
     * Cantidad absoluta tecleada. Para alimentos por unidad el valor es en UNIDADES
     * (se convierte a gramos con peso_unidad/racion); para granel, en gramos.
     *
     * Lo que no se entiende se rechaza y la cantidad se queda como estaba (fallo 4): antes
     * un «abc» o un «-50» la ponían a 0 g, con el alimento dentro de la comida sumando nada.
     */
    const handleFoodQuantitySet = (index, rawValue) => {
        const actual = tempFoods[index];
        if (!actual) return;
        const isUnit = Boolean(actual.por_unidad ?? actual.unidades);
        const peso = actual.peso_unidad || actual.racion || 100;
        const lectura = leerCantidad(rawValue, {
            porUnidad: isUnit,
            pesoUnidad: peso,
            minimo: isUnit ? peso : 1,
            alimento: actual,
        });
        if (lectura.estado === 'no_es_numero') {
            // Sin aviso mientras se escribe: el campo se queda vacío un instante al borrar
            // para teclear otra cifra, y un cartel por cada tecla sería insufrible. Se ignora
            // y la cantidad anterior sigue en pie.
            return;
        }
        if (lectura.estado === 'negativo') {
            toast.error(AVISO_NEGATIVO, { id: 'cantidad-negativa' });
            return;
        }
        if (lectura.estado === 'por_debajo_del_minimo') return;   // aún está escribiendo
        if (lectura.estado === 'pasa_del_tope') toast.warning(avisoDelTope(lectura.gramos), { id: 'tope-cantidad' });
        if (lectura.aviso) toast.warning(lectura.aviso, { id: 'tope-razonable' });
        let gramos = lectura.gramos;
        // EL POST: la bebida vegetal no pasa de los 4 g de grasa (lib/topeGrasaPost).
        if (isPostMode) {
            const tope = cantidadConTope(tempFoods, index, gramos);
            if (tope.frenada) {
                toast.info(tope.aviso || AVISO_TOPE_GRASA_POST, { id: 'tope-grasa-post' });
                // El campo enseña lo que de verdad queda, no lo tecleado.
                setQtyDraft(d => ({ ...d, [index]: isUnit ? tope.cantidad / peso : tope.cantidad }));
            }
            gramos = tope.cantidad;
        }
        setTempFoods(prev => prev.map((f, i) => (i === index ? recalcFoodMacros(f, gramos) : f)));
    };

    // EL INTRA SIN AMINOÁCIDOS (su primera pregunta, «¿los aminoácidos son obligatorios?»:
    // «yo le dejaría, pero avisando: "Te falta la proteína del intra"»).
    const intraSinProteina = isIntraMode && !tempFoods.some((f) => (f.macros_efectivos?.P || 0) > 0);
    const handleSaveAndClose = () => {
        if (!mealKey || tempFoods.length === 0) return;
        // En el intra no hay «¿Guardar la comida?»: «Guardar y cerrar» es la salida que dice
        // que ha terminado, y lo siguiente es la pregunta de dejarlo puesto. Solo se para si
        // falta la proteína.
        if ((isIntraMode && !intraSinProteina) || isPostMode) { guardarDeVerdad(); return; }
        // SIN «¿GUARDAR LA COMIDA?» SI NO HAY NADA QUE DECIR (Jesús, 28-09, con captura: fuera
        // esa pregunta). Pulsar «Guardar y cerrar» ya es decir que se guarda; la pregunta solo
        // se queda cuando trae un aviso de verdad: intra sin proteína, comida pasada o corta.
        const hayReparo = intraSinProteina || estadoDeLaComida?.tipo === 'pasado' || Boolean(seQuedaCortaDe);
        if (!hayReparo) { guardarDeVerdad(); return; }
        setConfirmando(true);
    };

    const guardarDeVerdad = () => {
        if (!mealKey || tempFoods.length === 0) return;
        setConfirmando(false);

        // tempFoods ya incluye los alimentos que había en la comida (precargados al abrir) más los
        // nuevos, así que REEMPLAZAMOS la lista (no concatenamos, o se duplicarían los existentes).
        setMealsData(prev => ({
            ...prev,
            [mealKey]: {
                ...prev[mealKey],
                alimentos: [...tempFoods]
            }
        }));

        // LA MISMA PALABRA Y SIN EMOJI (Jesús, 11-09). La confirmación de arriba dice
        // «3 ingredientes» y este aviso contestaba «✅ Comida guardada (3 alimentos)»: dos
        // nombres para lo mismo en la misma acción. Y el emoji no lo lleva ningún otro
        // aviso de la app.
        if (!isPeriMode) {
            toast.success(`Comida guardada · ${tempFoods.length} ${tempFoods.length === 1 ? 'ingrediente' : 'ingredientes'}`);
        }
        // Quien abrió la ventana decide lo de después (el intra: «¿lo dejamos puesto?»).
        onGuardado?.(mealKey, tempFoods.map((f) => ({ ...f })));
        onClose();
    };

    const getEmoji = getFoodEmoji || getFoodEmojiLocal;

    // Calma consejoParaEscogerAlimento: phase guidance text (not "Paso 1/2/3").
    const getPasoLabel = () => {
        // En el idioma del cliente, no del programa (Jesús, doc 21-08, apartado 14).
        if (isManual) return 'Modo manual - las cantidades las pones tú y lo compensas en el día';
        // EN EL INTRA, NADA: los tres pasos de abajo ya dicen qué se está eligiendo (Jesús,
        // «El intra, paso a paso», 27-09: «Alimentos Intra-entreno» pasa a ser el paso 1).
        if (isIntraMode) return null;
        if (isPostMode) return null;   // «Crea tu postentreno en tres pasos», abajo (27-09)
        // LOS LETREROS, CON SUS PALABRAS (Jesús, 10-09, «Los seis letreros»):
        //
        //     «La línea de arriba nombra el macro que manda ahora -- el primero de los dos
        //      que no haya cruzado el 80 %. Nada más.»
        //
        // Y dice lo que QUEDA, no un guión fijo: «después de la proteína no siempre tocan
        // hidratos: puede que sólo le falte grasa. La línea tiene que mirar lo que queda de
        // verdad». Por eso sale de `cruzoElOchenta` y no de un contador de pasos.
        //
        // LA REGLA VIVE EN `lib/elLetreroDelPaso`, CON SU PRUEBA. Son DOS listones -- el 80 %
        // para pasar de un macro al siguiente (Jesús, 10-09) y el margen para la última
        // línea, la de las preferencias (Francisco, 11-09, punto 3) -- y el caso que los
        // separa no se puede ver en pantalla sin un alimento a medida: hay que pillar los
        // hidratos por encima del 80 % y todavía a seis gramos. Con la regla suelta aquí no
        // había forma de probar que no se adelanta.
        return letreroDelPaso(served, target, MARGEN_VALIDO);
    };

    // Calma shows ALL category chips for the current phase (filtrosSinAlergias only removes
    // allergy categories); it NEVER hides a category because its foods don't fit the remaining.
    // Individual foods that overshoot are still excluded server-side (ajustarCantidad -> 0).
    // We mirror that: no emptiness-based chip hiding (was dropping most chips in paso 3, where
    // the remaining is tiny). filterAvoided() already applied the allergy removal upstream.
    const categories = getCurrentCategories();
    const rawFoods = isSearching ? searchResults : categoryFoods;
    // Manual: no suggestion ranking, so list alphabetically. Además, en manual ocultamos los
    // alimentos que ya están en la comida o ya añadidos en esta sesión del modal, para que no
    // vuelvan a aparecer en la lista (ya no se pueden volver a añadir, solo ajustar cantidad).
    const addedFoodIds = new Set([
        ...(mealsData[mealKey]?.alimentos || []).map(f => f.alimento_id),
        ...tempFoods.map(f => f.alimento_id),
    ]);
    // Buscando NO se reordena alfabéticamente ni en manual: el orden lo manda el parecido
    // con lo que se ha escrito, que es todo el arreglo del fallo 3.
    // EL GENÉRICO Y SU MARCA NO SALEN PEGADOS (Jesús, 11-09, punto 21).
    //
    // En las sugerencias salían seguidos «Queso fresco batido 0 % (Hacendado)» y «Queso
    // fresco batido 0 %», y más abajo dos «Aislado de suero». Parecen la lista repetida.
    //
    // No es un fallo del catálogo: que cada marca tenga su genérico es a propósito, y son
    // 159 productos. Lo que sobra es enseñar los dos JUNTOS cuando lo que se está haciendo
    // es elegir qué añadir; ahí basta con uno, y el mejor colocado es el que el motor ya ha
    // puesto delante.
    //
    // SOLO EN LAS SUGERENCIAS. Buscando NO se agrupa: quien escribe «queso fresco» quiere
    // ver todas sus marcas y comparar, que es justo para lo que busca.
    const unoPorProducto = (lista) => {
        const base = (n) => (n || '').replace(/\s*\([^)]*\)\s*$/, '').trim().toLowerCase();
        const vistos = new Set();
        return lista.filter((f) => {
            const k = base(f.nombre);
            if (!k) return true;
            if (vistos.has(k)) return false;
            vistos.add(k);
            return true;
        });
    };

    const displayFoods = isManual
        ? (isSearching
            ? rawFoods.filter(f => !addedFoodIds.has(f.id || f._id))
            : unoPorProducto([...rawFoods]
                .filter(f => !addedFoodIds.has(f.id || f._id))
                .sort((a, b) => (a.nombre || '').localeCompare(b.nombre || ''))))
        : (isSearching ? rawFoods : unoPorProducto(rawFoods));

    const { visibles: foodsVisibles, ocultos: foodsOcultos } = alimentosVisibles(displayFoods, {
        buscando: isSearching, verTodos,
    });

    // Preparations available for the selected category
    const availablePreparations = PREPARATIONS.filter(p => availablePreps.includes(p.value));

    return (
        <Dialog open={open} onOpenChange={() => onClose()}>
            <DialogContent className="max-w-2xl h-[90dvh] p-0 flex flex-col bg-card overflow-hidden">
                <DialogHeader className="flex-shrink-0 px-4 py-3 border-b">
                    {/* pr-8: deja hueco a la derecha para la cruz de cerrar (absolute right-4),
                        si no el switch Automático/Manual queda superpuesto a ella. */}
                    <div className="flex items-center justify-between gap-2 pr-8">
                        <div className="min-w-0">
                            <DialogTitle className="text-lg font-bold text-foreground">
                                {mealInfo?.label || `Comida ${mealKey?.replace('C', '')}`}
                            </DialogTitle>
                            {/* Jesús, 27-09: «y arriba, debajo de Comida Intra: Tu bebida
                                intraentreno, en 3 pasos:». */}
                            {isIntraMode && (
                                <p className="text-sm text-muted-foreground mt-0.5" data-testid="intra-en-3-pasos">
                                    Tu bebida intraentreno, en 3 pasos:
                                </p>
                            )}
                        </div>
                        {!isPeriMode && setMealMode && (
                            <div className="inline-flex rounded-full bg-muted p-0.5 shrink-0">
                                <button
                                    type="button"
                                    className={`px-2.5 py-1 text-xs font-semibold rounded-full transition-colors ${!isManual ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground'}`}
                                    onClick={() => setMealMode(mealKey, 'auto')}
                                    data-testid="modal-mode-auto"
                                >
                                    Automático
                                </button>
                                <button
                                    type="button"
                                    className={`px-2.5 py-1 text-xs font-semibold rounded-full transition-colors ${isManual ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground'}`}
                                    onClick={() => setMealMode(mealKey, 'manual')}
                                    data-testid="modal-mode-manual"
                                >
                                    Manual
                                </button>
                            </div>
                        )}
                    </div>
                    <DialogDescription className="sr-only">
                        Construye tu comida paso a paso
                    </DialogDescription>
                </DialogHeader>

                {/* La lista de alimentos tiene altura mínima garantizada; si las secciones fijas
                    no caben (móvil o ventana baja), la columna entera hace scroll. */}
                <div className="flex-1 min-h-0 flex flex-col overflow-y-auto">
                    {/* LO DE ARRIBA, JUNTO, PARA EL RECORRIDO (Jesús, 01-10: «que se vea hasta lo de
                        buscar»; Francisco, 02-10: «hasta donde empieza el listado de alimentos»):
                        el paso «Dos maneras de crear una comida» ilumina los números, el buscador
                        y las categorías a la vez. El envoltorio no cambia nada de cómo se ve. */}
                    <div className="flex-shrink-0" data-testid="montar-arriba">
                    {/* Macros summary */}
                    <div className="flex-shrink-0 px-4 py-2.5 bg-muted border-b" data-testid="montar-cabecera">
                        {/* ARRIBA DEL TODO Y EN SU COLOR (Jesús, 10-09). El verde no cierra la
                            puerta: el mensaje se queda mientras sigue metiendo cosas. */}
                        {estadoDeLaComida?.tipo === 'valida' && (
                            <div className="mb-2 rounded-lg px-3 py-2 bg-green-500/10 border border-green-500/30"
                                 data-testid="comida-valida">
                                <p className="text-xs font-bold text-green-700 dark:text-green-400">✓ Esta comida ya es válida</p>
                                <p className="text-[11px] text-muted-foreground mt-0.5">Puedes dejarla así o continuar añadiendo alimentos.</p>
                            </div>
                        )}
                        {estadoDeLaComida?.tipo === 'pasado' && (
                            <div className="mb-2 rounded-lg px-3 py-2 bg-red-500/10 border border-red-500/30"
                                 data-testid="comida-pasada">
                                <p className="text-xs font-bold text-red-700 dark:text-red-400">
                                    ⚠ Te has pasado de {NOMBRE_DEL_MACRO[estadoDeLaComida.macro]}
                                </p>
                                <p className="text-[11px] text-muted-foreground mt-0.5">
                                    Sobran {num1(estadoDeLaComida.sobra)} g. Baja la cantidad de algo o quítalo.
                                </p>
                            </div>
                        )}
                        <div className="text-xs font-medium text-muted-foreground mb-1.5 leading-tight">
                            {/* CON EL VERDE DELANTE, LA LÍNEA DE LA FASE CALLA (Gonzalo, 20-09):
                                «cuando sale el mensaje de arriba no tiene que salir el de abajo;
                                arriba está diciendo que puedes dejarla así y abajo, ahora termina
                                de cuadrar la comida con tus preferencias».

                                Las dos frases son verdad por separado -- la comida está dentro de
                                margen y aun así se puede seguir afinando -- pero juntas se
                                contradicen, y la que manda es el verde: es la que dice que ya
                                puede parar. La línea de la fase vuelve en cuanto deje de estarlo. */}
                            {/* Y CON EL AVISO DE PASARSE, TAMPOCO (Francisco, 24-09: «si sale la
                                alerta no debe salir el "ahora lo más importante"»). Arriba dice
                                que baje algo; «ahora lo más importante: la proteína» debajo le
                                manda a añadir. Manda el aviso. */}
                            {estadoDeLaComida?.tipo !== 'valida' && estadoDeLaComida?.tipo !== 'pasado' && getPasoLabel()}
                            {/* EL DOBLE AVISO («Proteína e hidratos cubiertos.») SE QUITÓ
                                (Jesús, 27-09: «sobra, eso quítalo»). Basta la frase de la fase:
                                «Ahora termina de cuadrar la comida con tus preferencias». */}
                            {/* Y CUANDO SE PASA, DÓNDE SE ARREGLA. Su estado 4 lleva esta
                                línea: el aviso dice que sobran hidratos, y esto dice dónde
                                tocarlos, que si no hay que adivinar que la lista de abajo
                                se abre. */}
                            {estadoDeLaComida?.tipo === 'pasado' && tempFoods.length > 0 && (
                                <span className="block mt-0.5 font-normal"
                                      data-testid="donde-se-arregla">
                                    Abajo puedes ver lo que llevas añadido y cambiar las cantidades.
                                </span>
                            )}
                        </div>
                        {/* Peri (intra/post) carry only P+H - Calma shows no Grasas column for them. */}
                        <div className={`grid ${isPeriMode ? 'grid-cols-2' : 'grid-cols-3'} gap-2 text-center leading-tight`}>
                            {(() => { const c = macroCell(served.P, target.P, "P"); return (
                                <div>
                                    <div className="text-xs text-muted-foreground">Proteína</div>
                                    {/* LOS NÚMEROS, EN BLANCO (Jesús, 11-09, punto 2). Iban proteína en naranja,
                                        hidratos en AZUL y grasa en amarillo, y debajo el estado otra vez
                                        en color: dos criterios pegados, y en la pantalla de al lado -- la
                                        de la comida -- ya estaba bien hecho. Su regla de siempre: «los
                                        tres números en blanco; el color, solo para el estado de debajo».
                                        Y el azul se va, que no existe en el resto de la app. */}
                                    <div className="text-lg font-bold leading-tight text-foreground">{c.num}</div>
                                    {/* «de 48» debajo del número, y el estado debajo de eso: la
                                        maqueta de Jesús del 10-09, tres líneas. */}
                                    <div className="text-[10px] text-muted-foreground leading-tight">{c.de}</div>
                                    {c.status && <div className={`text-[10px] font-semibold ${c.cls}`}>{c.status}</div>}
                                </div>
                            ); })()}
                            {(() => { const c = macroCell(served.H, target.H, "H"); return (
                                <div>
                                    <div className="text-xs text-muted-foreground">Hidratos</div>
                                    <div className="text-lg font-bold leading-tight text-foreground">{c.num}</div>
                                    {/* «de 48» debajo del número, y el estado debajo de eso: la
                                        maqueta de Jesús del 10-09, tres líneas. */}
                                    <div className="text-[10px] text-muted-foreground leading-tight">{c.de}</div>
                                    {c.status && <div className={`text-[10px] font-semibold ${c.cls}`}>{c.status}</div>}
                                </div>
                            ); })()}
                            {!isPeriMode && (() => { const c = macroCell(served.G, target.G, "G"); return (
                                <div>
                                    <div className="text-xs text-muted-foreground">Grasas</div>
                                    <div className="text-lg font-bold leading-tight text-foreground">{c.num}</div>
                                    {/* «de 48» debajo del número, y el estado debajo de eso: la
                                        maqueta de Jesús del 10-09, tres líneas. */}
                                    <div className="text-[10px] text-muted-foreground leading-tight">{c.de}</div>
                                    {c.status && <div className={`text-[10px] font-semibold ${c.cls}`}>{c.status}</div>}
                                </div>
                            ); })()}
                        </div>
                    </div>

                    {/* Search bar */}
                    <div className="flex-shrink-0 p-3 border-b">
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground w-4 h-4" />
                            <Input
                                placeholder="Buscar alimento..."
                                value={searchQuery}
                                onChange={(e) => handleSearch(e.target.value)}
                                className="pl-10 pr-10"
                            />
                            {searchQuery && (
                                <button
                                    onClick={() => { setSearchQuery(''); setIsSearching(false); setSearchResults([]); }}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-muted-foreground"
                                >
                                    <X className="w-4 h-4" />
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Category + Preparation Rails - hidden for intra (Calma: chips hidden) */}
                    {/* MIENTRAS SE ESCRIBE, FUERA LOS FILTROS (la regla común del artefacto del 23-09):
                        la búsqueda por texto mira todo el catálogo y no hace caso ni a la categoría
                        ni a la preparación, así que ahí solo quitaban sitio. Al borrar, vuelven. */}
                    {(isManual || !isCuadrada) && !isPeriMode && !searchQuery.trim() && <div className="flex-shrink-0 px-3 pt-3 pb-2 border-b bg-card space-y-2" data-testid="filtros-alimentos">
                        <BarraDeFiltros
                            categorias={selectedCategories}
                            preparaciones={PREPARATIONS.filter(pr => selectedPreparations.includes(pr.value))}
                            abierta={filtrosAbiertos}
                            haTocado={haTocadoCategoria}
                            onToggle={() => setFiltrosAbiertos(a => !a)}
                        />
                        {filtrosAbiertos && <>
                        {/* Dos filas visibles y el resto tras "Mostrar más".
                            Las categorías son círculos con icono, y el nombre sale al pasar por
                            encima y SE QUEDA abierto en la que está marcada -- en un móvil no hay
                            ratón, así que sin eso no habría forma de saber qué es cada dibujo.
                            El 09-08 se cambiaron por pastillas con el nombre dentro y se quitó ese
                            comportamiento; Francisco pidió volver atrás el 11-08. Los campos
                            `short` que se añadieron para aquello siguen en las listas de abajo,
                            sin usarse. */}
                        {/* FUERA LA PALABRA «CATEGORÍAS» (Jesús, 10-09, regla 2): «manda la
                            línea de lo que toca». El rótulo repetía lo que ya dicen los
                            iconos y le quitaba sitio al letrero que sí importa, el de qué
                            macro toca ahora. Sin `label` la tira se pinta sin cabecera. */}
                        <CategoryRail
                            categories={categories}
                            value={selectedCategories.map(c => c.id)}
                            onChange={handleCategoriesChange}
                            size="sm"
                            collapsible
                            maxRows={2}
                        />
                        {/* Y UNA LÍNEA QUE DIGA QUE SE PUEDE CAMBIAR (Jesús, 10-09, regla 3):
                            «si le pones los frecuentes de entrada y no le dices nada, no sabe
                            que puede cambiar de categoría. Con "toca un icono para cambiar de
                            categoría" basta».
                            Sólo mientras NO haya tocado ninguna: en cuanto elige una, ya sabe
                            que se puede y la línea sobra. Se mira si la ha TOCADO él, no si hay
                            alguna marcada: los frecuentes vienen marcados de entrada y con eso la
                            línea no salía nunca, justo en el caso para el que se pidió (23-09). */}
                        {!haTocadoCategoria && (
                            <p className="text-[11px] text-muted-foreground mt-1.5"
                               data-testid="pista-cambiar-categoria">
                                {selectedCategories.length ? 'Toca un icono para cambiar de categoría.' : 'Toca un icono para elegir una categoría.'}
                            </p>
                        )}
                        {selectedCategories.length > 0 && availablePreparations.length > 0 && (
                            <CategoryRail
                                label="Preparación"
                                categories={availablePreparations}
                                value={selectedPreparations}
                                onChange={(v) => { setSelectedPreparations(v); setVerTodos(false); }}
                                size="sm"
                                collapsible
                                maxRows={2}
                            />
                        )}
                        {/* Se pueden marcar varias categorías y varias preparaciones: tocar un
                            círculo no cierra el panel. Se cierra aquí o tocando la barra. */}
                        {/* «LISTO» SOLO CON ALGO MARCADO (Jesús, reunión del 25-09): «cuando está así
                            [sin nada marcado] el botón de listo simplemente sobra; ahí lo cierras
                            arriba». Si pincha un icono, sí vale. */}
                        {(selectedCategories.length > 0 || selectedPreparations.length > 0) && <div className="flex justify-end">
                            <button type="button" onClick={() => setFiltrosAbiertos(false)}
                                className="rounded-full bg-brand text-white text-xs font-bold px-4 py-1.5"
                                data-testid="filtros-listo">
                                Listo
                            </button>
                        </div>}
                        </>}
                    </div>}
                    {/* Fin de `montar-arriba`: hasta donde empieza la lista de alimentos (Francisco,
                        02-10: «el paso 12 debería iluminar hasta donde empieza el listado»). */}
                    </div>

                    {/* Food list. Scroll NATIVO (no el ScrollArea de Radix): su viewport
                        envuelve el contenido en un div display:table que ignora el ancho del
                        padre y hacía crecer las filas hasta el nombre más largo (en móvil se
                        cortaban contra el borde). Con overflow nativo el ancho se limita al
                        contenedor y los nombres largos saltan de línea. */}
                    <div className="flex-1 min-h-[38vh] overflow-y-auto">
                        <div className="p-3">
                            {/* EL INTRA Y EL POST, CON SUS TRES BLOQUES (Jesús, 25-09): sin buscar, lo
                                que sale son las sugerencias del método, no una lista por categorías. */}
                            {isPeriMode && !isManual && !isSearching ? (
                                <PeriSugerencias api={api} peri={isIntraMode ? 'intra' : 'post'}
                                    faltaP={Math.round(Math.max(0, remaining.P) * 10) / 10}
                                    faltaH={Math.round(Math.max(0, remaining.H) * 10) / 10}
                                    elegidos={tempFoods}
                                    onAdd={(f) => handleSelectFood(f, 1)} />
                            ) : !isSearching && selectedCategories.length === 0 ? (
                                <div className="text-center py-10 text-muted-foreground text-sm">
                                    Selecciona una categoría arriba para ver los alimentos
                                </div>
                            ) : (
                                <>
                                    {loadingFoods ? (
                                        <div className="text-center py-8 text-muted-foreground">Cargando...</div>
                                    ) : displayFoods.length === 0 ? (
                                        /* BUSCANDO Y SIN NADA, EL MISMO TEXTO QUE EN ALIMENTOS
                                           (punto 144 del 27-08: «igual en los dos sitios»).
                                           Los otros dos vacíos NO son éste y se quedan como
                                           están: uno es no tener frecuentes todavía y el otro
                                           una categoría vacía, y ninguno de los dos es «no lo
                                           tenemos». */
                                        /* LO QUE NO CABE NO SALE, Y SE DICE POR QUÉ (24-09). Como en
                                           Calma: «si escribes y no te sale nada, es probable que te
                                           pases». Si lo que ha escrito existe pero se pasaría, no es
                                           un alimento que falte en el catálogo: no se le ofrece
                                           pedirlo. */
                                        isSearching && fueraPorPasarse > 0 ? (
                                            <div className="text-center py-8 px-6" data-testid="busqueda-te-pasarias">
                                                <p className="font-semibold text-foreground mb-1">No cabe en lo que le queda a esta comida</p>
                                                <p className="text-sm text-muted-foreground">
                                                    Con la cantidad más pequeña ya te pasarías. Prueba con otro alimento o baja
                                                    la cantidad de alguno de los que llevas.
                                                </p>
                                            </div>
                                        ) : isSearching ? <SinResultados onPedir={() => setPedirAbierto(true)} restantes={peticionesQueQuedan} /> : (
                                        /* Y EL VACÍO DICE POR QUÉ ESTÁ VACÍO (Francisco,
                                           21-09). Aquí ponía «aún no tienes alimentos
                                           frecuentes» a quien SÍ los tiene: medido en su
                                           cuenta, quince. Lo que pasaba es que la búsqueda
                                           va con el hueco que le queda a la comida, y con la
                                           comida ya casi llena no cabe ninguno. Decirle que
                                           no tiene y que guarde dietas es mandarle a hacer
                                           algo que no le va a servir de nada.
                                           `sinHueco` es que los tres macros estén servidos:
                                           ahí no cabe nada, ni frecuente ni de categoría. */
                                        <div className="text-center py-8 text-muted-foreground">
                                            {(() => {
                                                const sinHueco = !isManual
                                                    && remaining.P <= 0 && remaining.H <= 0 && remaining.G <= 0;
                                                if (sinHueco) return 'Esta comida ya está servida: no cabe ningún alimento más sin pasarte.';
                                                return selectedCategories.some(c => c.id === '__frequent__')
                                                    ? 'Aún no tienes alimentos frecuentes - guarda algunas dietas primero'
                                                    : 'No hay alimentos en esta categoría';
                                            })()}
                                        </div>
                                        )
                                    ) : (
                                        <div className="space-y-1" data-testid="lista-alimentos">
                                            {/* DE DÓNDE SALE ESTA LISTA. Jesús, 15-08: «que lo que
                                                el cliente ha nombrado entre sí o sí, con las
                                                sugerencias aparte y marcadas como sugerencias».
                                                Buscando manda el texto; por categorías, lo que le
                                                falta a la comida -- y así se dice. */}
                                            <p className="text-[11px] text-muted-foreground mb-1.5" data-testid="origen-lista">
                                                {isSearching
                                                    ? <>Resultados de <span className="font-semibold text-foreground">«{searchQuery.trim()}»</span>{isManual ? '' : ', con la cantidad ya ajustada y los que empiezan por lo que escribiste primero'}</>
                                                    : isManual
                                                        ? 'Alimentos de la categoría, por orden alfabético'
                                                        // LA PRIMERA VEZ, QUÉ SON EL 80 Y EL 100 (Jesús, reunión
                                                        // del 25-09): «así la gente ya va a saber lo que es 80 y 100».
                                                        // Solo con la comida vacía y los dos botones a la vista.
                                                        : (mostrarOchenta && tempFoods.length === 0)
                                                            ? '100 te cuadra la proteína al 100%. 80 te la deja al 80% para que añadas otra fuente más.'
                                                            : 'Sugerencias para lo que le falta a esta comida, con la cantidad ya ajustada'}
                                            </p>
                                            {foodsVisibles.map((food, idx) => {
                                                const isFav = favorites.has(String(food.id));
                                                return (
                                                    <div key={food.id || idx} className="flex items-center gap-1">
                                                        {FOOD_FAVORITES_UI && (
                                                            <button
                                                                onClick={(e) => { e.stopPropagation(); toggleFavorite(food.id); }}
                                                                className={`flex-shrink-0 p-1 rounded transition-colors ${isFav ? 'text-yellow-400' : 'text-muted-foreground hover:text-yellow-300'}`}
                                                                data-testid={`fav-toggle-${food.id}`}
                                                            >
                                                                <Star className="w-4 h-4" fill={isFav ? 'currentColor' : 'none'} />
                                                            </button>
                                                        )}
                                                        <button
                                                            onClick={() => handleSelectFood(food, 1)}
                                                            className="flex-1 flex items-center gap-2 p-2 rounded-lg text-left transition-colors hover:bg-muted"
                                                            data-testid={`food-item-${food.id || idx}`}
                                                        >
                                                            <div className="flex-1 min-w-0">
                                                                <div className="text-sm text-foreground leading-snug break-words">
                                                                    <NombreConEtiqueta nombre={food.nombre} promocionado={food.is_promocionado} />
                                                                </div>
                                                                <div className="text-xs text-muted-foreground">
                                                                    {food._cantidad_sugerida
                                                                        // LAS DOS CANTIDADES cuando salen los dos botones, en su
                                                                        // orden: «192 g · 240 g» (Jesús, 10-09). Con un solo botón
                                                                        // se dice una sola, como siempre.
                                                                        // CADA CANTIDAD DEBAJO DE SU BOTÓN (Jesús, 11-09, punto 5).
                                                                        // Aquí iban las dos juntas -- «424 g · 530 g» -- y no se sabía
                                                                        // cuál era de cuál. Con los dos botones, esta línea ya no dice
                                                                        // la cantidad en gramos: cada una va bajo el suyo. La de
                                                                        // unidades sí se queda, que es otra cosa -- cuántas piezas son
                                                                        // --, y entonces la flecha tiene que seguir ahí.
                                                                        ? (() => {
                                                                            const porUnidad = (food.por_unidad ?? food.unidades)
                                                                                && (food.peso_unidad || food.racion) > 0;
                                                                            const delante = porUnidad
                                                                                ? `${num1(Math.round(food._cantidad_sugerida / (food.peso_unidad || food.racion) * 2) / 2)} ud (${num1(food._cantidad_sugerida)} g/ml)`
                                                                                : (mostrarOchenta ? '' : `${num1(food._cantidad_sugerida)} g`);
                                                                            return delante ? `${delante} → ` : '';
                                                                        })()
                                                                        // Sin cantidad del motor (comida manual, sin objetivo que cuadrar):
                                                                        // se dice a cuánto equivale una unidad, que es lo que se añadirá.
                                                                        : (food.por_unidad ?? food.unidades) && (food.peso_unidad || food.racion) > 0
                                                                            ? `1 ud (${num1(food.peso_unidad || food.racion)} g/ml) → `
                                                                            : `${num1(food.racion || 100)} g → `}
                                                                    {(() => {
                                                                        // Sin cantidad sugerida (comida manual) se enseñan los macros
                                                                        // de UNA ración, que es lo que trae el catálogo ya pasado
                                                                        // por las reglas del método (`macros_efectivos`).
                                                                        const ms = food._macros_sugeridos || food.macros_efectivos;
                                                                        const qty = food._cantidad_sugerida || food.racion || 100;
                                                                        const fmt = num1;
                                                                        const p = ms?.P ?? (food.proteinas || 0) * qty / 100;
                                                                        const h = ms?.H ?? (food.hidratos || 0) * qty / 100;
                                                                        const g = ms?.G ?? (food.grasas || 0) * qty / 100;
                                                                        // SIN COLOR, Y SIN AZUL (Jesús, 11-09, punto 2). Iban P naranja,
                                                                        // H azul y G amarillo, igual que la cabecera; y su regla es que
                                                                        // los números van en blanco y el color se guarda para el estado.
                                                                        // Aquí no hay estado que contar -- esto es de qué está hecho el
                                                                        // alimento --, así que no lleva color ninguno. La letra delante
                                                                        // ya dice cuál es cuál.
                                                                        const parts = [
                                                                            p > 0 ? <span key="p" className="font-semibold text-foreground">P={fmt(p)} g</span> : null,
                                                                            h > 0 ? <span key="h" className="font-semibold text-foreground">H={fmt(h)} g</span> : null,
                                                                            g > 0 ? <span key="g" className="font-semibold text-foreground">G={fmt(g)} g</span> : null,
                                                                        ].filter(Boolean);
                                                                        if (parts.length === 0) return 'No aporta macros';
                                                                        return parts.reduce((acc, el, i) => (i === 0 ? [el] : [...acc, ' ', el]), []);
                                                                    })()}
                                                                </div>
                                                            </div>
                                                        </button>
                                                        {/* EL 80 Y EL 100 (Jesús, 10-09). Van fuera de la fila para que
                                                            se pueda elegir cantidad sin abrir nada. El «80» sólo mientras
                                                            ninguno de los dos macros haya cruzado su 80 %; después, sólo
                                                            el 100. Ver `mostrarOchenta`. */}
                                                        {/* UNO RELLENO Y OTRO CON BORDE (Jesús, 11-09, punto 5). Eran dos
                                                            botones grises idénticos, sin decir qué hacían: «el 80 relleno en
                                                            naranja -- el paso corto -- y el 100 con borde naranja -- el largo
                                                            --, para que se vea de un vistazo que uno es más que el otro». Y
                                                            cada cantidad debajo de su botón, que antes iban las dos juntas
                                                            arriba y no se sabía cuál era cuál. */}
                                                        <>
                                                        {mostrarOchenta && food._cantidad_sugerida ? (
                                                            <div className="flex-shrink-0 flex flex-col items-center gap-0.5">
                                                                <button
                                                                    onClick={(e) => { e.stopPropagation(); handleSelectFood(food, EL_OCHENTA); }}
                                                                    data-testid={`add-80-${food.id || idx}`}
                                                                    className="h-8 px-2.5 rounded-lg bg-brand-orange text-white text-xs font-bold hover:brightness-110 transition"
                                                                >80</button>
                                                                <span className="text-[10px] text-muted-foreground leading-none">
                                                                    {num1(ochentaDe(food))} g
                                                                </span>
                                                            </div>
                                                        ) : null}
                                                        <div className="flex-shrink-0 flex flex-col items-center gap-0.5">
                                                            <button
                                                                onClick={(e) => { e.stopPropagation(); handleSelectFood(food, 1); }}
                                                                data-testid={`add-100-${food.id || idx}`}
                                                                className={`h-8 px-2.5 rounded-lg text-xs font-bold transition-colors ${
                                                                    mostrarOchenta && food._cantidad_sugerida
                                                                        ? 'border-2 border-brand-orange text-brand-orange hover:bg-brand-orange/10'
                                                                        : 'border border-border text-muted-foreground hover:border-brand-orange hover:text-brand-orange'}`}
                                                            >{food._cantidad_sugerida ? '100' : <Plus className="w-4 h-4" />}</button>
                                                            {mostrarOchenta && food._cantidad_sugerida ? (
                                                                <span className="text-[10px] text-muted-foreground leading-none">
                                                                    {num1(food._cantidad_sugerida)} g
                                                                </span>
                                                            ) : null}
                                                        </div>
                                                        </>
                                                    </div>
                                                );
                                            })}
                                            {foodsOcultos > 0 && (
                                                <button type="button" onClick={() => setVerTodos(true)}
                                                    data-testid="mostrar-mas-alimentos"
                                                    className="w-full mt-1 py-2 rounded-lg border border-border text-xs font-semibold text-muted-foreground hover:text-foreground hover:border-foreground/40">
                                                    Mostrar más ({foodsOcultos})
                                                </button>
                                            )}
                                            {/* Y SE PUEDE VOLVER A RECOGER (Jesús, 01-10, vídeo de las 22:48: «le das
                                                a mostrar más y no se puede quitar, aquí hay un fallo»). */}
                                            {verTodos && !isSearching && displayFoods.length > CUANTOS_ALIMENTOS && (
                                                <button type="button" onClick={() => setVerTodos(false)}
                                                    data-testid="mostrar-menos-alimentos"
                                                    className="w-full mt-1 py-2 rounded-lg border border-border text-xs font-semibold text-muted-foreground hover:text-foreground hover:border-foreground/40">
                                                    Mostrar menos
                                                </button>
                                            )}
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    </div>

                </div>

                {/* Added foods - barra SIEMPRE visible, anclada fuera del scroll (encima del botón de guardar) */}
                {tempFoods.length > 0 && (() => {
                        const tot = tempFoods.reduce((a, f) => ({
                            P: a.P + (f.macros_efectivos?.P || 0),
                            H: a.H + (f.macros_efectivos?.H || 0),
                            G: a.G + (f.macros_efectivos?.G || 0),
                        }), { P: 0, H: 0, G: 0 });
                        const fmt = v => Math.round(v);
                        return (
                            <div className={`border-t bg-muted flex flex-col ${addedOpen ? 'min-h-0 max-h-52' : 'flex-shrink-0'}`} data-testid="ingredientes-anadidos">
                                <button
                                    type="button"
                                    onClick={() => setAddedOpen(o => !o)}
                                    className="flex-shrink-0 w-full flex items-center justify-between px-3 py-2.5 text-sm font-medium text-foreground"
                                    data-testid="added-bar-toggle"
                                >
                                    <span className="flex items-center gap-2">
                                        <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-black text-white text-xs">{tempFoods.length}</span>
                                        {/* SIN «AÑADIDOS» (Francisco, 21-09): «el listado dice
                                            añadidos y los macros, quita añadidos». El número en
                                            la bolita ya dice cuántos son; la palabra solo
                                            empujaba los macros, que es lo que se mira. */}
                                        {/* CON EL COLOR DEL ESTADO (Trello de JDG, 24-09): «los macros
                                            de arriba se van al hacer scroll», así que abajo, que se
                                            ve siempre, cada número lleva el mismo color que su
                                            estado de arriba -- verde si ya vale, rojo si se pasa,
                                            en blanco mientras falta --. Sale de `macroCell`, la
                                            misma celda de la cabecera: no hay una segunda regla. */}
                                        <span className="text-xs font-normal" data-testid="added-bar-macros">
                                            {(isPeriMode ? ['P', 'H'] : ['P', 'H', 'G']).map((k, i) => {
                                                const cls = macroCell(served[k], target[k], k).cls || 'text-foreground';
                                                return (
                                                    <React.Fragment key={k}>
                                                        {i > 0 && ' · '}
                                                        <span className={`font-semibold ${cls}`}
                                                            data-testid={`added-bar-${k}`}>{k} {fmt(tot[k])}</span>
                                                    </React.Fragment>
                                                );
                                            })}
                                        </span>
                                    </span>
                                    <span className="inline-flex items-center gap-1 rounded-full border border-brand/40 bg-brand/10 px-2.5 py-1 text-xs font-semibold text-brand flex-shrink-0">
                                        {addedOpen ? 'Ocultar' : 'Ver'}
                                        <ChevronUp className={`w-3.5 h-3.5 transition-transform ${addedOpen ? '' : 'rotate-180'}`} />
                                    </span>
                                </button>
                                {addedOpen && (
                                    <div className="flex-1 min-h-0 overflow-y-auto px-3 pb-3 space-y-1">
                                        {tempFoods.map((food, idx) => (
                                            <div key={idx} className="flex items-center gap-2 bg-card rounded p-2 text-sm">
                                                {/* El nombre entero en el title: recortado con
                                                    puntos suspensivos no hay forma de saber qué
                                                    es «Salmón ahumado (Ha…» (Jesús, fallo 44).

                                                    Y ENCIMA SE LEE (ficha 33 del parte de
                                                    Gonzalo, 4-09-2026): «nombres de alimento
                                                    cortados a "Ta…"; sitio, o un corte
                                                    legible». El `title` es una ayuda que en el
                                                    teléfono NO EXISTE -- no hay ratón que
                                                    posar --, así que aquí el nombre era «Ta…» y
                                                    punto. El `truncate` de una línea peleaba
                                                    con el stepper y la papelera en una fila de
                                                    390 px. Ahora parte por palabras y admite
                                                    dos líneas, que es donde caben los nombres
                                                    del catálogo («Tortitas de arroz»); el
                                                    `title` se queda para el ordenador. */}
                                                <span className="flex-1 min-w-0 break-words line-clamp-2 text-foreground" title={food.nombre}>{food.nombre}</span>
                                                <div className="flex items-center gap-1">
                                                    <button
                                                        onClick={() => handleFoodQuantityChange(idx, -1)}
                                                        data-testid={`temp-menos-${idx}`}
                                                        className="w-6 h-6 flex items-center justify-center bg-muted rounded"
                                                    >
                                                        <Minus className="w-3 h-3" />
                                                    </button>
                                                    {(() => {
                                                        const isUnit = (food.por_unidad ?? food.unidades) && (food.peso_unidad || food.racion) > 0;
                                                        const grams = food.cantidad_g || food.cantidad || 0;
                                                        const display = isUnit
                                                            ? Math.round((grams / (food.peso_unidad || food.racion)) * 2) / 2
                                                            : grams;
                                                        const value = qtyDraft[idx] !== undefined ? qtyDraft[idx] : display;
                                                        return (
                                                            <span className="flex items-center gap-1">
                                                                {/* Con techo: el campo no tenía `max` y la flecha de arriba subía
                                                                    sin final. El techo es el tope duro, en la unidad del campo;
                                                                    el tope razonable del alimento avisa aparte. */}
                                                                <input
                                                                    type="number"
                                                                    min="0"
                                                                    step={pasoDelCampo(food, isUnit)}
                                                                    value={value}
                                                                    onChange={(e) => { setQtyDraft(d => ({ ...d, [idx]: e.target.value })); handleFoodQuantitySet(idx, e.target.value); }}
                                                                    onBlur={() => setQtyDraft(d => { const n = { ...d }; delete n[idx]; return n; })}
                                                                    onFocus={(e) => e.target.select()}
                                                                    className="w-12 text-center text-xs bg-muted rounded px-1 py-1 text-foreground focus:outline-none focus:ring-1 focus:ring-[#FF671F]"
                                                                />
                                                                {/* Los gramos de LO QUE HAY, no los de una unidad suelta: con medio
                                                                    plátano en el plato ponía «0,5 ud (100 g)» y quien lo lee entiende
                                                                    que medio plátano son 100 g (QA del 15-08 en producción). */}
                                                                <span className="text-[10px] text-muted-foreground whitespace-nowrap">
                                                                    {isUnit ? `ud (${num1(grams)} g)` : (food.es_liquido ? 'ml' : 'g')}
                                                                </span>
                                                            </span>
                                                        );
                                                    })()}
                                                    <button
                                                        onClick={() => handleFoodQuantityChange(idx, 1)}
                                                        data-testid={`temp-mas-${idx}`}
                                                        className="w-6 h-6 flex items-center justify-center bg-muted rounded"
                                                    >
                                                        <Plus className="w-3 h-3" />
                                                    </button>
                                                    <button
                                                        onClick={() => handleRemoveFood(idx)}
                                                        className="w-6 h-6 flex items-center justify-center text-red-500"
                                                    >
                                                        <X className="w-4 h-4" />
                                                    </button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        );
                })()}

                {/* LA CONFIRMACIÓN AL GUARDAR (Jesús, 10-09). Va por encima de la comida,
                    sin desmontarla: si dice «Continuar» vuelve exactamente a donde estaba,
                    «sin perder nada».

                    OJO CON EL `relative`: este `absolute inset-0` se ancla al propio
                    `DialogContent`, que Radix ya pinta `position: fixed`. Ponerle además
                    `relative` a ese contenedor -- como estuvo unas horas el 11-09 -- PISA el
                    `fixed` y el modal deja de estar centrado en la pantalla: en el móvil no
                    se nota porque ocupa todo, y en el escritorio se va hacia abajo y sale
                    cortado por arriba. No hace falta: `fixed` ya es ancestro posicionado. */}
                {confirmando && (
                    <div className="absolute inset-0 z-50 flex items-end justify-center bg-black/60 p-4"
                         data-testid="confirmar-guardar">
                        <div className="w-full max-w-md rounded-2xl bg-card border p-5 shadow-xl">
                            <p className="text-base font-bold text-foreground">
                                ¿Guardar la {mealInfo?.label || `Comida ${mealKey?.replace('C', '')}`}?
                            </p>
                            <p className="text-sm text-muted-foreground mt-2">
                                Se queda con lo que tienes ahora: {tempFoods.length}{' '}
                                {tempFoods.length === 1 ? 'ingrediente' : 'ingredientes'}
                                {estadoDeLaComida?.tipo === 'valida' ? ', y la comida es válida.' : '.'}
                            </p>
                            {/* Y SI NO ES VÁLIDA, QUE LO DIGA AQUÍ. «Es el único sitio donde
                                ese aviso hace falta.» */}
                            {/* Y TAMBIÉN SI SE PASA (23-09): con «Te has pasado de grasa» arriba,
                                la confirmación decía solo «4 ingredientes.», como si valiera. */}
                            {intraSinProteina && (
                                <p className="text-sm font-semibold text-amber-600 dark:text-amber-400 mt-2"
                                   data-testid="intra-sin-proteina">
                                    Te falta la proteína del intra. ¿Lo guardas así?
                                </p>
                            )}
                            {estadoDeLaComida?.tipo === 'pasado' ? (
                                <p className="text-sm font-semibold text-amber-600 dark:text-amber-400 mt-2"
                                   data-testid="reparo-al-guardar">
                                    Esta comida se pasa de {NOMBRE_DEL_MACRO[estadoDeLaComida.macro]}: sobran {num1(estadoDeLaComida.sobra)} g. ¿La dejas así?
                                </p>
                            ) : seQuedaCortaDe && (
                                <p className="text-sm font-semibold text-amber-600 dark:text-amber-400 mt-2"
                                   data-testid="reparo-al-guardar">
                                    Esta comida se queda corta de {NOMBRE_DEL_MACRO[seQuedaCortaDe.macro]}. ¿La dejas así?
                                </p>
                            )}
                            <div className="flex gap-2 mt-4">
                                {/* «CONTINUAR», NO «SEGUIR MONTANDO» (Jesús, 11-09, punto 7):
                                    «montar» se le había vuelto a colar y él lo tiene dicho. */}
                                <Button variant="outline" className="flex-1 h-11 rounded-full"
                                        onClick={() => setConfirmando(false)}
                                        data-testid="seguir-montando">
                                    Continuar
                                </Button>
                                <Button className="flex-1 h-11 rounded-full bg-black hover:bg-gray-900 text-white"
                                        onClick={guardarDeVerdad}
                                        data-testid="confirmar-guardar-si">
                                    Guardar
                                </Button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Footer */}
                <div className="flex-shrink-0 bg-card border-t p-4">
                    {tempFoods.length > 0 ? (
                        <Button
                            onClick={handleSaveAndClose}
                            className={`w-full h-12 rounded-full font-bold ${
                                isCuadrada
                                    ? 'bg-green-500 hover:bg-green-600 text-white'
                                    : 'bg-black hover:bg-gray-900 text-white'
                            }`}
                            data-testid="save-build-meal"
                        >
                            {isCuadrada && !isPeriMode ? '🎉 GUARDAR COMIDA CUADRADA' : 'GUARDAR Y CERRAR'}
                        </Button>
                    ) : (
                        <Button
                            variant="outline"
                            onClick={onClose}
                            className="w-full h-12 rounded-full"
                        >
                            Cancelar
                        </Button>
                    )}
                </div>
            </DialogContent>
            {/* EL FORMULARIO DE PEDIR ALIMENTO, EL MISMO QUE EL DE ALIMENTOS. Va fuera del
                `DialogContent` y dentro del `Dialog`: montado dentro, la ventana de la
                comida le recorta el alto y el formulario se queda sin sitio para sus dos
                fotos. Al mandarlo se cierra solo y la comida sigue donde estaba. */}
            <SuggestFoodModal open={pedirAbierto} onClose={() => setPedirAbierto(false)}
                onSubmitted={() => setPedirAbierto(false)} />
        </Dialog>
    );
};

export default BuildMealModal;
